import React, { useState, useEffect, useCallback } from 'react';
import * as faceapi from 'face-api.js';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import SignupModal from './SignupModal';
import LoginModal from './LoginModal';
import BTCGiftCardVsATMSection from './BTCGiftCardVsATMSection';
import ActivateCardModal from './ActivateCardModal';
import CurrencyConverterWidget from './components/CurrencyConverterWidget';
import ProfileModal from './ProfileModal';
import PinVerificationModal from './components/PinVerificationModal';
import apiService, { authAPI, walletAPI, cardAPI, hotWalletAPI } from './apiService';
import { CreditCard, Wallet, Plus, Send, Eye, EyeOff, LogOut, User, RefreshCw, ArrowDown, ArrowUp, Clock, DollarSign, Camera, Upload, Settings, Bell, MapPin, Shield, Copy, Check, X, Lock, Zap, Globe, Star, Award, Smartphone, CreditCard as CardIcon, Building, Users, TrendingUp, CheckCircle, AlertTriangle, Layers, Database, FileText, PhoneCall, Mail, MapPin as Location, QrCode, Download, Menu, ChevronDown, ArrowLeftRight, History, Repeat, ShoppingCart } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { supabase } from './supabaseClient';
// Import all page components
import ServicesPage from './pages/Services';
import ActivatedCardsList from './components/ActivatedCardsList';
import CompanyPage from './pages/Company';
import SupportPage from './pages/Support';
import LegalPage from './pages/Legal';
import SendMoneyPage from './pages/Send';
import CryptoCardShop from './pages/CryptoCardShop';
import DigitalWalletsPage from './pages/DigitalWallet';
import BusinessSolutionsPage from './pages/BusinessSolution';
import AboutUsPage from './pages/AboutUs';
import CareersPage from './pages/Careers';
import PressPage from './pages/Press';
import SecurityPage from './pages/Security';
import TermsOfServicePage from './pages/Terms-Service';
import PrivacyPolicyPage from './pages/PrivacyPolicy';
import APIDocumentationPage from './pages/APIDocumentation';
import InvestorsPage from './pages/Investor';
import LicensePage from './pages/License';
import HelpCenterPage from './pages/HelpCenter';
import CompliancePage from './pages/Compliance';
import ContactUsPage from './pages/Contact';
import CryptoCardsPage from './pages/CryptoCards';
import AdminPanel from './AdminPanel';
import BatchPaymentsPage from './pages/BatchPayments'; // ✅ Batch Payments

const API_URL = process.env.REACT_APP_API_URL || 'https://globalpay-backend-v2.onrender.com';


const ZANORSApp = () => {
  const [user, setUser] = useState(null);
  const [walletAddresses, setWalletAddresses] = useState({});
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [cryptoCards, setCryptoCards] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [hotWalletBalance, setHotWalletBalance] = useState(0);
  const [balances, setBalances] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [cryptoPrices, setCryptoPrices] = useState({ BTC: 90000, ETH: 3000, USDC: 1, USDT: 1, BNB: 600, XRP: 0.5 });
  const [showBalances, setShowBalances] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [isLoadingWallets, setIsLoadingWallets] = useState(false);  // ✅ Prevent concurrent loadWallets calls
  const [showKYCModal, setShowKYCModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [show3DFaceModal, setShow3DFaceModal] = useState(false);
  const [showSelfieModal, setShowSelfieModal] = useState(false);
  const [selfieStream, setSelfieStream] = useState(null);
  const [faceCaptures, setFaceCaptures] = useState({
    front: null,
    left: null,
    right: null
  });
  const [captureStep, setCaptureStep] = useState('front');
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [videoStream, setVideoStream] = useState(null);
  const [faceDetectionReady, setFaceDetectionReady] = useState(false);
  const [detectedFaceBox, setDetectedFaceBox] = useState(null);
  const [ovalDimensions, setOvalDimensions] = useState({ rx: 18, ry: 50 });
  // Load face-api models
  useEffect(() => {
    const loadModels = async () => {
      try {
        const MODEL_URL = '/models';
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        ]);
        setFaceDetectionReady(true);
        console.log('Face detection models loaded');
      } catch (error) {
        console.error('Error loading face detection models:', error);
      }
    };
    loadModels();
  }, []);
  // Real-time face detection
  useEffect(() => {
    if (!faceDetectionReady || !videoStream || !videoRef.current) return;

    const detectFace = async () => {
      const video = videoRef.current;
      if (!video || video.readyState !== 4) return;

      try {
        const detection = await faceapi
          .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions())
          .withFaceLandmarks();

        if (detection) {
          const box = detection.detection.box;
          setDetectedFaceBox(box);

          // Calculate dynamic oval size (30% padding around face)
          const videoWidth = video.videoWidth;
          const videoHeight = video.videoHeight;
          const padding = 1.3;

          const faceWidthPercent = ((box.width * padding) / videoWidth) * 100;
          const faceHeightPercent = ((box.height * padding) / videoHeight) * 100;

          setOvalDimensions({
            rx: Math.max(15, Math.min(faceWidthPercent / 2, 25)),
            ry: Math.max(20, Math.min(faceHeightPercent / 2, 50)),
          });
        } else {
          setDetectedFaceBox(null);
        }
      } catch (error) {
        console.error('Face detection error:', error);
      }
    };

    const interval = setInterval(detectFace, 100);
    return () => clearInterval(interval);
  }, [faceDetectionReady, videoStream]);
  
  const [showAddBankModal, setShowAddBankModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pendingTransaction, setPendingTransaction] = useState(null);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [showLandingPage, setShowLandingPage] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showActivateCardModal, setShowActivateCardModal] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [showQRCode, setShowQRCode] = useState(false);
  const [loginStep, setLoginStep] = useState('email');
  const [signupStep, setSignupStep] = useState('details');
  const [selectedCrypto, setSelectedCrypto] = useState('BTC');
  const [showSecurityWarning, setShowSecurityWarning] = useState(false);
  const [showPrivateKeyModal, setShowPrivateKeyModal] = useState(false);
  const [selectedWalletForKey, setSelectedWalletForKey] = useState(null);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [revealedKeys, setRevealedKeys] = useState({});
  const [loadingPrivateKey, setLoadingPrivateKey] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState({});
  const [showSendCryptoModal, setShowSendCryptoModal] = useState(false);
  const [showReceiveCryptoModal, setShowReceiveCryptoModal] = useState(false);
  const [showConvertCryptoModal, setShowConvertCryptoModal] = useState(false);
  const [showCryptoToCashModal, setShowCryptoToCashModal] = useState(false);
  const [showCreateWalletModal, setShowCreateWalletModal] = useState(false);
  const [showTopUpCardModal, setShowTopUpCardModal] = useState(false);
  const [showCardHistoryModal, setShowCardHistoryModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [transactionFilter, setTransactionFilter] = useState('all');
  const [showSendFromCardModal, setShowSendFromCardModal] = useState(false);
  const [showWithdrawFromCardModal, setShowWithdrawFromCardModal] = useState(false);
  const [selectedCardWallet, setSelectedCardWallet] = useState(null);
  const [loginData, setLoginData] = useState({
    email: '',
    phone: '',
    loginMethod: 'email',
    code: '',
    generatedCode: ''
  });
 const [signupData, setSignupData] = useState({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  country: 'US',
  password: '',           // ← ADD THIS
  confirmPassword: '',    // ← ADD THIS
  code: '',
  generatedCode: ''
});
  const [activateData, setActivateData] = useState({
    cardNumber: '',
    pin: '',
    code: '',
    generatedCode: ''
  });
  const [exchangeRates, setExchangeRates] = useState({});
  const [pin, setPin] = useState('');
  const [pendingAction, setPendingAction] = useState(null);
  const [copied, setCopied] = useState('');
  const [spendingLimit, setSpendingLimit] = useState({ daily: 900, monthly: 5000 });
  const [showKYCUpgrade, setShowKYCUpgrade] = useState(false);
  const [showPinSetup, setShowPinSetup] = useState(false);
  const [pinSetupData, setPinSetupData] = useState({
    pin: '',
    confirmPin: '',
    password: ''
  });
  const [pinError, setPinError] = useState('');

  const countries = [
    { code: 'DZ', name: 'Algeria', flag: '🇩🇿', requiresSSN: false },
    { code: 'AO', name: 'Angola', flag: '🇦🇴', requiresSSN: false },
    { code: 'AR', name: 'Argentina', flag: '🇦🇷', requiresSSN: false },
    { code: 'AU', name: 'Australia', flag: '🇦🇺', requiresSSN: false },
    { code: 'BS', name: 'Bahamas', flag: '🇧🇸', requiresSSN: false },
    { code: 'BH', name: 'Bahrain', flag: '🇧🇭', requiresSSN: false },
    { code: 'BD', name: 'Bangladesh', flag: '🇧🇩', requiresSSN: false },
    { code: 'BB', name: 'Barbados', flag: '🇧🇧', requiresSSN: false },
    { code: 'BO', name: 'Bolivia', flag: '🇧🇴', requiresSSN: false },
    { code: 'BW', name: 'Botswana', flag: '🇧🇼', requiresSSN: false },
    { code: 'BR', name: 'Brazil', flag: '🇧🇷', requiresSSN: false },
    { code: 'BG', name: 'Bulgaria', flag: '🇧🇬', requiresSSN: false },
    { code: 'KH', name: 'Cambodia', flag: '🇰🇭', requiresSSN: false },
    { code: 'CA', name: 'Canada', flag: '🇨🇦', requiresSSN: false },
    { code: 'CL', name: 'Chile', flag: '🇨🇱', requiresSSN: false },
    { code: 'CN', name: 'China', flag: '🇨🇳', requiresSSN: false },
    { code: 'CO', name: 'Colombia', flag: '🇨🇴', requiresSSN: false },
    { code: 'CD', name: 'Congo (DRC)', flag: '🇨🇩', requiresSSN: false },
    { code: 'CR', name: 'Costa Rica', flag: '🇨🇷', requiresSSN: false },
    { code: 'HR', name: 'Croatia', flag: '🇭🇷', requiresSSN: false },
    { code: 'CU', name: 'Cuba', flag: '🇨🇺', requiresSSN: false },
    { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿', requiresSSN: false },
    { code: 'DK', name: 'Denmark', flag: '🇩🇰', requiresSSN: false },
    { code: 'DO', name: 'Dominican Republic', flag: '🇩🇴', requiresSSN: false },
    { code: 'EG', name: 'Egypt', flag: '🇪🇬', requiresSSN: false },
    { code: 'ET', name: 'Ethiopia', flag: '🇪🇹', requiresSSN: false },
    { code: 'FJ', name: 'Fiji', flag: '🇫🇯', requiresSSN: false },
    { code: 'FR', name: 'France', flag: '🇫🇷', requiresSSN: false },
    { code: 'GM', name: 'Gambia', flag: '🇬🇲', requiresSSN: false },
    { code: 'DE', name: 'Germany', flag: '🇩🇪', requiresSSN: false },
    { code: 'GH', name: 'Ghana', flag: '🇬🇭', requiresSSN: false },
    { code: 'GT', name: 'Guatemala', flag: '🇬🇹', requiresSSN: false },
    { code: 'HT', name: 'Haiti', flag: '🇭🇹', requiresSSN: false },
    { code: 'HN', name: 'Honduras', flag: '🇭🇳', requiresSSN: false },
    { code: 'HK', name: 'Hong Kong', flag: '🇭🇰', requiresSSN: false },
    { code: 'HU', name: 'Hungary', flag: '🇭🇺', requiresSSN: false },
    { code: 'IN', name: 'India', flag: '🇮🇳', requiresSSN: false },
    { code: 'ID', name: 'Indonesia', flag: '🇮🇩', requiresSSN: false },
    { code: 'IQ', name: 'Iraq', flag: '🇮🇶', requiresSSN: false },
    { code: 'IL', name: 'Israel', flag: '🇮🇱', requiresSSN: false },
    { code: 'JM', name: 'Jamaica', flag: '🇯🇲', requiresSSN: false },
    { code: 'JP', name: 'Japan', flag: '🇯🇵', requiresSSN: false },
    { code: 'JO', name: 'Jordan', flag: '🇯🇴', requiresSSN: false },
    { code: 'KE', name: 'Kenya', flag: '🇰🇪', requiresSSN: false },
    { code: 'KW', name: 'Kuwait', flag: '🇰🇼', requiresSSN: false },
    { code: 'LA', name: 'Laos', flag: '🇱🇦', requiresSSN: false },
    { code: 'LB', name: 'Lebanon', flag: '🇱🇧', requiresSSN: false },
    { code: 'LR', name: 'Liberia', flag: '🇱🇷', requiresSSN: false },
    { code: 'MW', name: 'Malawi', flag: '🇲🇼', requiresSSN: false },
    { code: 'MY', name: 'Malaysia', flag: '🇲🇾', requiresSSN: false },
    { code: 'MU', name: 'Mauritius', flag: '🇲🇺', requiresSSN: false },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽', requiresSSN: false },
    { code: 'MA', name: 'Morocco', flag: '🇲🇦', requiresSSN: false },
    { code: 'MM', name: 'Myanmar', flag: '🇲🇲', requiresSSN: false },
    { code: 'NA', name: 'Namibia', flag: '🇳🇦', requiresSSN: false },
    { code: 'NP', name: 'Nepal', flag: '🇳🇵', requiresSSN: false },
    { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', requiresSSN: false },
    { code: 'NI', name: 'Nicaragua', flag: '🇳🇮', requiresSSN: false },
    { code: 'NG', name: 'Nigeria', flag: '🇳🇬', requiresSSN: false },
    { code: 'NO', name: 'Norway', flag: '🇳🇴', requiresSSN: false },
    { code: 'OM', name: 'Oman', flag: '🇴🇲', requiresSSN: false },
    { code: 'PK', name: 'Pakistan', flag: '🇵🇰', requiresSSN: false },
    { code: 'PA', name: 'Panama', flag: '🇵🇦', requiresSSN: false },
    { code: 'PG', name: 'Papua New Guinea', flag: '🇵🇬', requiresSSN: false },
    { code: 'PY', name: 'Paraguay', flag: '🇵🇾', requiresSSN: false },
    { code: 'PE', name: 'Peru', flag: '🇵🇪', requiresSSN: false },
    { code: 'PH', name: 'Philippines', flag: '🇵🇭', requiresSSN: false },
    { code: 'PL', name: 'Poland', flag: '🇵🇱', requiresSSN: false },
    { code: 'QA', name: 'Qatar', flag: '🇶🇦', requiresSSN: false },
    { code: 'RO', name: 'Romania', flag: '🇷🇴', requiresSSN: false },
    { code: 'RU', name: 'Russia', flag: '🇷🇺', requiresSSN: false },
    { code: 'RW', name: 'Rwanda', flag: '🇷🇼', requiresSSN: false },
    { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', requiresSSN: false },
    { code: 'SL', name: 'Sierra Leone', flag: '🇸🇱', requiresSSN: false },
    { code: 'SG', name: 'Singapore', flag: '🇸🇬', requiresSSN: false },
    { code: 'ZA', name: 'South Africa', flag: '🇿🇦', requiresSSN: false },
    { code: 'KR', name: 'South Korea', flag: '🇰🇷', requiresSSN: false },
    { code: 'LK', name: 'Sri Lanka', flag: '🇱🇰', requiresSSN: false },
    { code: 'SE', name: 'Sweden', flag: '🇸🇪', requiresSSN: false },
    { code: 'CH', name: 'Switzerland', flag: '🇨🇭', requiresSSN: false },
    { code: 'TW', name: 'Taiwan', flag: '🇹🇼', requiresSSN: false },
    { code: 'TZ', name: 'Tanzania', flag: '🇹🇿', requiresSSN: false },
    { code: 'TH', name: 'Thailand', flag: '🇹🇭', requiresSSN: false },
    { code: 'TT', name: 'Trinidad and Tobago', flag: '🇹🇹', requiresSSN: false },
    { code: 'TN', name: 'Tunisia', flag: '🇹🇳', requiresSSN: false },
    { code: 'TR', name: 'Turkey', flag: '🇹🇷', requiresSSN: false },
    { code: 'UG', name: 'Uganda', flag: '🇺🇬', requiresSSN: false },
    { code: 'UA', name: 'Ukraine', flag: '🇺🇦', requiresSSN: false },
    { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪', requiresSSN: false },
    { code: 'UK', name: 'United Kingdom', flag: '🇬🇧', requiresSSN: false },
    { code: 'US', name: 'United States', flag: '🇺🇸', requiresSSN: true },
    { code: 'UY', name: 'Uruguay', flag: '🇺🇾', requiresSSN: false },
    { code: 'VE', name: 'Venezuela', flag: '🇻🇪', requiresSSN: false },
    { code: 'VN', name: 'Vietnam', flag: '🇻🇳', requiresSSN: false },
    { code: 'ZM', name: 'Zambia', flag: '🇿🇲', requiresSSN: false }
  ];

  const currencies = [
    { code: 'AED', symbol: 'د.إ', flag: '🇦🇪', name: 'UAE Dirham' },
    { code: 'DZD', symbol: 'د.ج', flag: '🇩🇿', name: 'Algerian Dinar' },
    { code: 'AOA', symbol: 'Kz', flag: '🇦🇴', name: 'Angolan Kwanza' },
    { code: 'ARS', symbol: '$', flag: '🇦🇷', name: 'Argentine Peso' },
    { code: 'AUD', symbol: 'A$', flag: '🇦🇺', name: 'Australian Dollar' },
    { code: 'BSD', symbol: 'B$', flag: '🇧🇸', name: 'Bahamian Dollar' },
    { code: 'BHD', symbol: 'د.ب', flag: '🇧🇭', name: 'Bahraini Dinar' },
    { code: 'BDT', symbol: '৳', flag: '🇧🇩', name: 'Bangladeshi Taka' },
    { code: 'BBD', symbol: 'Bds$', flag: '🇧🇧', name: 'Barbadian Dollar' },
    { code: 'BOB', symbol: 'Bs', flag: '🇧🇴', name: 'Bolivian Boliviano' },
    { code: 'BWP', symbol: 'P', flag: '🇧🇼', name: 'Botswana Pula' },
    { code: 'BRL', symbol: 'R$', flag: '🇧🇷', name: 'Brazilian Real' },
    { code: 'GBP', symbol: '£', flag: '🇬🇧', name: 'British Pound' },
    { code: 'BGN', symbol: 'лв', flag: '🇧🇬', name: 'Bulgarian Lev' },
    { code: 'KHR', symbol: '៛', flag: '🇰🇭', name: 'Cambodian Riel' },
    { code: 'CAD', symbol: 'C$', flag: '🇨🇦', name: 'Canadian Dollar' },
    { code: 'XAF', symbol: 'FCFA', flag: '🇨🇲', name: 'Central African CFA Franc' },
    { code: 'CLP', symbol: '$', flag: '🇨🇱', name: 'Chilean Peso' },
    { code: 'CNY', symbol: '¥', flag: '🇨🇳', name: 'Chinese Yuan' },
    { code: 'COP', symbol: '$', flag: '🇨🇴', name: 'Colombian Peso' },
    { code: 'CDF', symbol: 'FC', flag: '🇨🇩', name: 'Congolese Franc' },
    { code: 'CRC', symbol: '₡', flag: '🇨🇷', name: 'Costa Rican Colon' },
    { code: 'HRK', symbol: 'kn', flag: '🇭🇷', name: 'Croatian Kuna' },
    { code: 'CUP', symbol: '₱', flag: '🇨🇺', name: 'Cuban Peso' },
    { code: 'CZK', symbol: 'Kč', flag: '🇨🇿', name: 'Czech Koruna' },
    { code: 'DKK', symbol: 'kr', flag: '🇩🇰', name: 'Danish Krone' },
    { code: 'DOP', symbol: 'RD$', flag: '🇩🇴', name: 'Dominican Peso' },
    { code: 'XCD', symbol: 'EC$', flag: '🇱🇨', name: 'East Caribbean Dollar' },
    { code: 'EGP', symbol: 'E£', flag: '🇪🇬', name: 'Egyptian Pound' },
    { code: 'ETB', symbol: 'Br', flag: '🇪🇹', name: 'Ethiopian Birr' },
    { code: 'EUR', symbol: '€', flag: '🇪🇺', name: 'Euro' },
    { code: 'FJD', symbol: 'FJ$', flag: '🇫🇯', name: 'Fijian Dollar' },
    { code: 'GMD', symbol: 'D', flag: '🇬🇲', name: 'Gambian Dalasi' },
    { code: 'GHS', symbol: '₵', flag: '🇬🇭', name: 'Ghanaian Cedi' },
    { code: 'GTQ', symbol: 'Q', flag: '🇬🇹', name: 'Guatemalan Quetzal' },
    { code: 'HTG', symbol: 'G', flag: '🇭🇹', name: 'Haitian Gourde' },
    { code: 'HKD', symbol: 'HK$', flag: '🇭🇰', name: 'Hong Kong Dollar' },
    { code: 'HNL', symbol: 'L', flag: '🇭🇳', name: 'Honduran Lempira' },
    { code: 'HUF', symbol: 'Ft', flag: '🇭🇺', name: 'Hungarian Forint' },
    { code: 'INR', symbol: '₹', flag: '🇮🇳', name: 'Indian Rupee' },
    { code: 'IDR', symbol: 'Rp', flag: '🇮🇩', name: 'Indonesian Rupiah' },
    { code: 'IQD', symbol: 'ع.د', flag: '🇮🇶', name: 'Iraqi Dinar' },
    { code: 'ILS', symbol: '₪', flag: '🇮🇱', name: 'Israeli Shekel' },
    { code: 'JMD', symbol: 'J$', flag: '🇯🇲', name: 'Jamaican Dollar' },
    { code: 'JPY', symbol: '¥', flag: '🇯🇵', name: 'Japanese Yen' },
    { code: 'JOD', symbol: 'د.ا', flag: '🇯🇴', name: 'Jordanian Dinar' },
    { code: 'KES', symbol: 'KSh', flag: '🇰🇪', name: 'Kenyan Shilling' },
    { code: 'KWD', symbol: 'د.ك', flag: '🇰🇼', name: 'Kuwaiti Dinar' },
    { code: 'LAK', symbol: '₭', flag: '🇱🇦', name: 'Lao Kip' },
    { code: 'LBP', symbol: 'ل.ل', flag: '🇱🇧', name: 'Lebanese Pound' },
    { code: 'LRD', symbol: 'L$', flag: '🇱🇷', name: 'Liberian Dollar' },
    { code: 'MWK', symbol: 'MK', flag: '🇲🇼', name: 'Malawian Kwacha' },
    { code: 'MYR', symbol: 'RM', flag: '🇲🇾', name: 'Malaysian Ringgit' },
    { code: 'MUR', symbol: '₨', flag: '🇲🇺', name: 'Mauritian Rupee' },
    { code: 'MXN', symbol: 'Mex$', flag: '🇲🇽', name: 'Mexican Peso' },
    { code: 'MAD', symbol: 'DH', flag: '🇲🇦', name: 'Moroccan Dirham' },
    { code: 'MMK', symbol: 'K', flag: '🇲🇲', name: 'Myanmar Kyat' },
    { code: 'NAD', symbol: 'N$', flag: '🇳🇦', name: 'Namibian Dollar' },
    { code: 'NPR', symbol: 'Rs', flag: '🇳🇵', name: 'Nepalese Rupee' },
    { code: 'NZD', symbol: 'NZ$', flag: '🇳🇿', name: 'New Zealand Dollar' },
    { code: 'NIO', symbol: 'C$', flag: '🇳🇮', name: 'Nicaraguan Cordoba' },
    { code: 'NGN', symbol: '₦', flag: '🇳🇬', name: 'Nigerian Naira' },
    { code: 'NOK', symbol: 'kr', flag: '🇳🇴', name: 'Norwegian Krone' },
    { code: 'OMR', symbol: 'ر.ع', flag: '🇴🇲', name: 'Omani Rial' },
    { code: 'PKR', symbol: '₨', flag: '🇵🇰', name: 'Pakistani Rupee' },
    { code: 'PAB', symbol: 'B/.', flag: '🇵🇦', name: 'Panamanian Balboa' },
    { code: 'PGK', symbol: 'K', flag: '🇵🇬', name: 'Papua New Guinean Kina' },
    { code: 'PYG', symbol: '₲', flag: '🇵🇾', name: 'Paraguayan Guarani' },
    { code: 'PEN', symbol: 'S/', flag: '🇵🇪', name: 'Peruvian Sol' },
    { code: 'PHP', symbol: '₱', flag: '🇵🇭', name: 'Philippine Peso' },
    { code: 'PLN', symbol: 'zł', flag: '🇵🇱', name: 'Polish Zloty' },
    { code: 'QAR', symbol: 'ر.ق', flag: '🇶🇦', name: 'Qatari Riyal' },
    { code: 'RON', symbol: 'lei', flag: '🇷🇴', name: 'Romanian Leu' },
    { code: 'RUB', symbol: '₽', flag: '🇷🇺', name: 'Russian Ruble' },
    { code: 'RWF', symbol: 'RF', flag: '🇷🇼', name: 'Rwandan Franc' },
    { code: 'SAR', symbol: 'ر.س', flag: '🇸🇦', name: 'Saudi Riyal' },
    { code: 'SLL', symbol: 'Le', flag: '🇸🇱', name: 'Sierra Leonean Leone' },
    { code: 'SGD', symbol: 'S$', flag: '🇸🇬', name: 'Singapore Dollar' },
    { code: 'ZAR', symbol: 'R', flag: '🇿🇦', name: 'South African Rand' },
    { code: 'KRW', symbol: '₩', flag: '🇰🇷', name: 'South Korean Won' },
    { code: 'LKR', symbol: 'Rs', flag: '🇱🇰', name: 'Sri Lankan Rupee' },
    { code: 'SEK', symbol: 'kr', flag: '🇸🇪', name: 'Swedish Krona' },
    { code: 'CHF', symbol: 'CHF', flag: '🇨🇭', name: 'Swiss Franc' },
    { code: 'TWD', symbol: 'NT$', flag: '🇹🇼', name: 'Taiwan Dollar' },
    { code: 'TZS', symbol: 'TSh', flag: '🇹🇿', name: 'Tanzanian Shilling' },
    { code: 'THB', symbol: '฿', flag: '🇹🇭', name: 'Thai Baht' },
    { code: 'TTD', symbol: 'TT$', flag: '🇹🇹', name: 'Trinidad and Tobago Dollar' },
    { code: 'TND', symbol: 'د.ت', flag: '🇹🇳', name: 'Tunisian Dinar' },
    { code: 'TRY', symbol: '₺', flag: '🇹🇷', name: 'Turkish Lira' },
    { code: 'UGX', symbol: 'USh', flag: '🇺🇬', name: 'Ugandan Shilling' },
    { code: 'UAH', symbol: '₴', flag: '🇺🇦', name: 'Ukrainian Hryvnia' },
    { code: 'UYU', symbol: '$U', flag: '🇺🇾', name: 'Uruguayan Peso' },
    { code: 'USD', symbol: '$', flag: '🇺🇸', name: 'US Dollar' },
    { code: 'VES', symbol: 'Bs', flag: '🇻🇪', name: 'Venezuelan Bolivar' },
    { code: 'VND', symbol: '₫', flag: '🇻🇳', name: 'Vietnamese Dong' },
    { code: 'XOF', symbol: 'CFA', flag: '🇨🇮', name: 'West African CFA Franc' },
    { code: 'ZMW', symbol: 'ZK', flag: '🇿🇲', name: 'Zambian Kwacha' }
  ];

  const cryptoOptions = [
    { code: 'BTC', name: 'Bitcoin', symbol: '₿', color: 'orange' },
    { code: 'ETH', name: 'Ethereum', symbol: 'Ξ', color: 'blue' },
    { code: 'USDC', name: 'USD Coin', symbol: '$', color: 'green' },
    { code: 'USDT', name: 'Tether', symbol: '₮', color: 'teal' },
    { code: 'BNB', name: 'Binance Coin', symbol: 'BNB', color: 'yellow' },
    { code: 'XRP', name: 'Ripple', symbol: 'XRP', color: 'gray' }
  ];

  const menuItems = {
    Services: [
      { name: 'Send Money', path: '/send-money' },
      { name: 'Crypto Cards', path: '/crypto-cards' },
      { name: 'Digital Wallets', path: '/digital-wallets' },
      { name: 'Business Solutions', path: '/business-solutions' }
    ],
    Company: [
      { name: 'About Us', path: '/about-us' },
      { name: 'Careers', path: '/careers' },
      { name: 'Press', path: '/press' },
      { name: 'Investors', path: '/investors' }
    ],
    Support: [
      { name: 'Help Center', path: '/help-center' },
      { name: 'Contact Us', path: '/contact-us' },
      { name: 'API Documentation', path: '/api-documentation' },
      { name: 'Security', path: '/security' }
    ],
    Legal: [
      { name: 'Terms of Service', path: '/terms-of-service' },
      { name: 'Privacy Policy', path: '/privacy-policy' },
      { name: 'Compliance', path: '/compliance' },
      { name: 'Licenses', path: '/licenses' }
    ]
  };

  const toggleSubmenu = (menu) => {
    setExpandedMenu(expandedMenu === menu ? null : menu);
  };

  const [transferData, setTransferData] = useState({
    recipientEmail: '',
    fromCurrency: '',
    toCurrency: '',
    amount: '',
    message: '',
    paymentMethod: 'crypto',
    selectedCrypto: 'BTC',
    transferType: 'international',
    bankAccount: '',
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
    zipCode: ''
  });

  const [showFromCurrencyDropdown, setShowFromCurrencyDropdown] = useState(false);
  const [showToCurrencyDropdown, setShowToCurrencyDropdown] = useState(false);
  const [currencySearch, setCurrencySearch] = useState('');

  const [sendCryptoData, setSendCryptoData] = useState({
    sendFrom: '', // wallet or card ID to send from
    crypto: 'BTC',
    recipientAddress: '',
    amount: '',
    note: ''
  });

  const [convertCryptoData, setConvertCryptoData] = useState({
    fromCrypto: 'BTC',
    toCrypto: 'ETH',
    amount: ''
  });

  const [cryptoToCashData, setCryptoToCashData] = useState({
    crypto: 'BTC',
    amount: '',
    currency: 'USD',
    withdrawMethod: 'bank'
  });

  const [cardData, setCardData] = useState({
    cardNumber: '',
    pin: ''
  });

  const [withdrawData, setWithdrawData] = useState({
    amount: '',
    method: 'bank',
    currency: 'USD'
  });

  const [topupData, setTopupData] = useState({
    amount: '',
    method: 'external',
    crypto: 'BTC',
    buyMethod: 'bank',
    cardAmount: '',
    paymentMethod: 'card'
  });

  const [walletSendData, setWalletSendData] = useState({
    crypto: 'BTC',
    recipientAddress: '',
    amount: '',
    note: ''
  });

  const [walletWithdrawData, setWalletWithdrawData] = useState({
    crypto: 'BTC',
    amount: '',
    method: 'bank'
  });

  const [profileData, setProfileData] = useState({
    firstName: 'Test',
    lastName: 'Phone',
    email: 'testphone@example.com',
    phone: '+1234567890',
    profilePicture: null,
    notifications: {
      email: true,
      sms: false,
      push: true,
      transactions: true
    }
  });

  const [kycData, setKycData] = useState({
    country: 'US',
    dateOfBirth: '',
    governmentId: null,
    selfieWithId: null,
    faceCapture: null
  });

  const [newBankData, setNewBankData] = useState({
    bankName: '',
    accountNumber: '',
    routingNumber: '',
    accountType: 'checking'
  });

  const [newWalletData, setNewWalletData] = useState({
    walletName: '',
    walletType: 'standard'
  });

  const handleSignupDataChange = useCallback((field, value) => {
    setSignupData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleLoginDataChange = useCallback((field, value) => {
    setLoginData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleActivateDataChange = useCallback((field, value) => {
    setActivateData(prev => ({ ...prev, [field]: value }));
  }, []);

  const [bankAccounts, setBankAccounts] = useState([
    { id: '1', bankName: 'Chase Bank', accountNumber: '****1234', type: 'checking' },
    { id: '2', bankName: 'Bank of America', accountNumber: '****5678', type: 'savings' }
  ]);
  
  const [creditCards, setCreditCards] = useState([]);
  const fetchExchangeRates = async () => {
    try {
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
      const data = await response.json();
      
      console.log('✅ Fetched exchange rates from API');
      
      // Apply 1% markup (reduce rate by 1%) for profit margin
      // Users see slightly worse rates, we profit from the spread
      const adjustedRates = {};
      Object.keys(data.rates).forEach(currency => {
        // For currencies where USD buys more (rate > 1), reduce by 1%
        // For currencies where USD buys less (rate < 1), increase by 1%
        // This ensures we always profit on the spread
        if (data.rates[currency] > 1) {
          adjustedRates[currency] = data.rates[currency] * 0.99; // 1% less favorable for user
        } else {
          adjustedRates[currency] = data.rates[currency] * 1.01; // 1% less favorable for user
        }
      });
      
      console.log('💰 Applied 1% profit margin to rates');
      setExchangeRates(adjustedRates);
    } catch (error) {
      console.error('Failed to fetch exchange rates, using fallback:', error);
      // Comprehensive fallback rates with 1% markup already applied
      const fallbackRates = {
        // Base
        USD: 1.0,
        // North America
        CAD: 1.2375, MXN: 19.899,
        // Europe
        EUR: 0.8415, GBP: 0.7227, CHF: 0.9108, NOK: 8.811, SEK: 8.613, DKK: 6.237,
        PLN: 3.861, CZK: 21.285, HUF: 292.05, RON: 4.554, BGN: 1.7919, HRK: 6.8976,
        RUB: 91.08, TRY: 26.73, UAH: 36.63,
        // Asia
        CNY: 7.1775, JPY: 108.9, INR: 73.755, KRW: 1168.2, SGD: 1.3365,
        HKD: 7.7517, TWD: 27.7227, THB: 33.0033, MYR: 4.1559, PHP: 49.995,
        IDR: 14257.5, VND: 22770, PKR: 275.22, BDT: 84.15, LKR: 297,
        NPR: 118.8, MMK: 2079, KHR: 4039.5, LAK: 16830,
        // Middle East
        AED: 3.63, SAR: 3.7125, QAR: 3.6036, KWD: 0.30393, BHD: 0.37323,
        OMR: 0.38115, JOD: 0.70329, ILS: 3.2967, IQD: 1297.35, LBP: 14850,
        // Africa
        ZAR: 14.652, NGN: 406.89, EGP: 30.69, KES: 106.92, GHS: 6.039,
        TZS: 2475, UGX: 3682.8, ETB: 54.945, MAD: 9.999, DZD: 133.65,
        TND: 3.069, AOA: 824.175, XAF: 548.46, XOF: 548.46, ZMW: 18.315,
        BWP: 13.3155, MUR: 44.55, NAD: 17.8365, RWF: 1019.7, MWK: 811.8,
        GMD: 58.41, CDF: 1980, SLL: 19701.9, LRD: 153.153,
        // South America
        BRL: 5.148, ARS: 346.5, CLP: 891, COP: 3960, PEN: 3.7125,
        VES: 35.64, UYU: 39.6, PYG: 7227, BOB: 6.831,
        // Oceania
        AUD: 1.3365, NZD: 1.5543, FJD: 2.2275, PGK: 3.7125,
        // Caribbean & Central America
        JMD: 154.44, TTD: 6.732, BBD: 2.0, BSD: 0.99, XCD: 2.6730,
        CRC: 534.6, GTQ: 7.7517, HNL: 24.4827, NIO: 36.4, PAB: 0.99,
        DOP: 59.4, CUP: 24, HTG: 130.68,
        // Others
        ISK: 136.125, AFN: 69.3, ALL: 92.97, AMD: 386.1, AZN: 1.683,
        GEL: 2.6433, KZT: 445.5, UZS: 11385, KGS: 83.655, TJS: 10.593,
        TMT: 3.465, MNT: 3366
      };
      setExchangeRates(fallbackRates);
    }
  };

  const fetchCryptoPrices = async () => {
    try {
      const response = await fetch("https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,usd-coin,tether,binancecoin,ripple&vs_currencies=usd");
      const data = await response.json();
      const prices = {
        BTC: data.bitcoin?.usd || 45000,
        ETH: data.ethereum?.usd || 3000,
        USDC: data["usd-coin"]?.usd || 1,
        USDT: data.tether?.usd || 1,
        BNB: data.binancecoin?.usd || 300,
        XRP: data.ripple?.usd || 0.50
      };
      setCryptoPrices(prices);
      return prices;
    } catch (error) {
      console.error("Failed to fetch crypto prices:", error);
      const fallback = {
        BTC: 45000,
        ETH: 3000,
        USDC: 1,
        USDT: 1,
        BNB: 300,
        XRP: 0.50
      };
      setCryptoPrices(fallback);
      return fallback;
    }
  };

  // ✅ NUCLEAR FIX: Aggressively remove duplicate wallets whenever wallets state changes
  useEffect(() => {
    if (wallets.length === 0) return;
    
    const walletIds = wallets.map(w => w.id);
    const uniqueIds = new Set(walletIds);
    
    // If there are duplicates, deduplicate immediately
    if (walletIds.length !== uniqueIds.size) {
      console.log('🚨 DUPLICATE WALLETS DETECTED! Deduplicating...');
      console.log('  Before:', wallets.map(w => ({ id: w.id, name: w.name, btc: w.balances?.BTC })));
      
      const seen = new Set();
      const deduplicated = wallets.filter(wallet => {
        if (seen.has(wallet.id)) {
          console.log('  ❌ Removing duplicate:', wallet.id, wallet.name);
          return false;
        }
        seen.add(wallet.id);
        return true;
      });
      
      console.log('  After:', deduplicated.map(w => ({ id: w.id, name: w.name, btc: w.balances?.BTC })));
      setWallets(deduplicated);
      
      // Also clean localStorage
      try {
        const walletsToSave = deduplicated.filter(w => w.id !== 'hot-wallet-system');
        localStorage.setItem('globalpay_wallets', JSON.stringify(walletsToSave));
        console.log('  💾 Cleaned localStorage');
      } catch (e) {
        console.warn('  Failed to clean localStorage:', e);
      }
    }
  }, [wallets]);


 useEffect(() => {
    // Immediately restore data from localStorage if available (before API calls)
    try {
      const savedBalances = localStorage.getItem('globalpay_balances');
      const savedCards = localStorage.getItem('globalpay_cards');
      const savedWallets = localStorage.getItem('globalpay_wallets');
      
      if (savedBalances) {
        const parsedBalances = JSON.parse(savedBalances);
        setBalances({
          ...parsedBalances,
          USDC: 0,
          ETH: 0,
          USDT: 0,
          BNB: 0,
          XRP: 0
        });
        console.log('💾 Restored balances from localStorage on mount');
      }
      
      if (savedCards) {
        setCryptoCards(JSON.parse(savedCards));
        console.log('💾 Restored cards from localStorage on mount');
      }
      
      if (savedWallets) {
        const parsedWallets = JSON.parse(savedWallets);
        // ✅ Clean out old system 'hot-wallet-system' ID if it exists (from old code)
        const filteredWallets = parsedWallets.filter(w => w.id !== 'hot-wallet-system');
        
        // ✅ If we removed any old system hot wallets, save the cleaned version back
        if (filteredWallets.length !== parsedWallets.length) {
          console.log('🧹 Cleaning', parsedWallets.length - filteredWallets.length, 'old system hot_wallet(s) from localStorage');
          localStorage.setItem('globalpay_wallets', JSON.stringify(filteredWallets));
        }
        
        setWallets(filteredWallets);
        console.log('💾 Restored wallets from localStorage on mount');
      }
    } catch (error) {
      console.warn('Failed to restore from localStorage on mount:', error);
    }
    
    // Fetch exchange rates on mount (don't block other initialization)
    fetchExchangeRates().catch(err => {
      console.warn('Failed to fetch exchange rates on mount:', err);
    });
    
    // Check for active session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        loadUserFromSupabase(session.user.id);
        setShowLandingPage(false);
      }
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        loadUserFromSupabase(session.user.id);
        setShowLandingPage(false);
      } else {
        setShowLandingPage(true);
        setUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Close currency dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      setShowFromCurrencyDropdown(false);
      setShowToCurrencyDropdown(false);
    };

    if (showFromCurrencyDropdown || showToCurrencyDropdown) {
      // Add a small delay to prevent catching the click that opened the dropdown
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 10);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showFromCurrencyDropdown, showToCurrencyDropdown]);

  const loadUserFromSupabase = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) throw error;

    setUser({
      id: data.id,
      firstName: data.first_name,
      lastName: data.last_name,
      email: data.email,
      phone: data.phone,
      role: data.role,  // ✅ FIX: Include role from database
      kycLevel: data.kyc_level,
      remainingLimit: data.remaining_limit,
      totalTransactionAmount: data.total_transaction_amount,
      profilePicture: data.profile_picture
    });
    
    console.log('✅ User loaded from Supabase:', { 
      userId: data.id, 
      email: data.email, 
      role: data.role,
      kycLevel: data.kyc_level 
    });  // Debug

    setProfileData({
      firstName: data.first_name,
      lastName: data.last_name,
      email: data.email,
      phone: data.phone,
      profilePicture: data.profile_picture,
      notifications: {
        email: true,
        sms: false,
        push: true,
        transactions: true
      }
    });

    // Load REAL data from backend
    await loadRealDataFromBackend();
  } catch (error) {
    console.error('Error loading user:', error);
  }
};
const loadRealDataFromBackend = async () => {
  try {
    // Fetch live crypto prices
    const prices = await fetchCryptoPrices();
    
    // Fetch exchange rates (don't let this break wallet loading)
    try {
      await fetchExchangeRates();
    } catch (rateError) {
      console.warn('Exchange rates failed, using fallback:', rateError);
    }
    
    // Get real wallet data from backend
    const walletResponse = await walletAPI.getWallet();
    
    // Load activated cards to calculate total balance
    const cardsResponse = await cardAPI.getCards();
    const cards = cardsResponse.success ? cardsResponse.data.cards : [];
    
    // ✅ Get ALL wallets for accurate total (no double counting!)
    let allWalletsBTC = 0;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // Query ALL wallets owned by this user
        const { data: allWallets } = await supabase
          .from('wallets')
          .select('btc_balance')
          .eq('user_id', session.user.id);
        
        if (allWallets && allWallets.length > 0) {
          // Sum up all wallet balances
          allWalletsBTC = allWallets.reduce((sum, w) => sum + (parseFloat(w.btc_balance) || 0), 0);
          console.log(`✅ Total from ${allWallets.length} wallet(s): ${allWalletsBTC.toFixed(8)} BTC`);
        }
      }
    } catch (walletsError) {
      console.warn('Failed to get all wallets for total calculation:', walletsError);
    }
    
    // Calculate total BTC from ALL wallets + all cards (no double counting!)
    const cardsBTC = cards.reduce((sum, card) => sum + (parseFloat(card.balance) || 0), 0);
    const totalBTC = allWalletsBTC + cardsBTC;  // ✅ Fixed: No more double counting!
    const totalUSD = totalBTC * prices.BTC;
    
    console.log('💰 Balance Calculation:');
    console.log('  - All Wallets BTC:', allWalletsBTC.toFixed(8));
    console.log('  - Cards BTC:', cardsBTC.toFixed(8));
    console.log('  - Total BTC:', totalBTC.toFixed(8));
    console.log('  - BTC Price:', prices.BTC);
    console.log('  - Total USD:', totalUSD);
    console.log('  - Number of cards:', cards.length);
    
    // Set balances
    setBalances({
      BTC: totalBTC,
      USD: walletResponse.success ? walletResponse.data?.usd_balance || 0 : 0,
      USDC: 0,
      ETH: 0,
      USDT: 0,
      BNB: 0,
      XRP: 0,
      totalUSD: totalUSD,
      prices: prices
    });
    
    // Store cards
    setCryptoCards(cards);
    
    // Load wallets
    console.log('🔄 About to call loadWallets()...');
    await loadWallets();
    console.log('✅ loadWallets() completed');
    
    // Get real transactions from backend
    // Get real transactions from backend
try {
  const txResponse = await walletAPI.getTransactions();
  if (txResponse.success) {
    setTransactions(txResponse.data.transactions || []);
  }
} catch (txError) {
  console.warn('Transactions failed (endpoint may not exist yet):', txError);
  setTransactions([]); // Don't break dashboard
}
    
    // Save data to localStorage for persistence
    try {
      localStorage.setItem('globalpay_balances', JSON.stringify({
        BTC: totalBTC,
        USD: walletResponse.success ? walletResponse.data?.usd_balance || 0 : 0,
        totalUSD: totalUSD,
        prices: prices
      }));
      localStorage.setItem('globalpay_cards', JSON.stringify(cards));
      console.log('💾 Data saved to localStorage');
    } catch (storageError) {
      console.warn('Failed to save to localStorage:', storageError);
    }
    
    console.log('✅ loadRealDataFromBackend completed successfully');
  } catch (error) {
    console.error("❌ Error loading backend data:", error);
    console.error("Error stack:", error.stack);
    
    // Try to load from localStorage backup
    try {
      const savedBalances = localStorage.getItem('globalpay_balances');
      const savedCards = localStorage.getItem('globalpay_cards');
      
      if (savedBalances) {
        const parsedBalances = JSON.parse(savedBalances);
        setBalances({
          ...parsedBalances,
          USDC: 0,
          ETH: 0,
          USDT: 0,
          BNB: 0,
          XRP: 0
        });
        console.log('✅ Restored balances from localStorage');
      }
      
      if (savedCards) {
        setCryptoCards(JSON.parse(savedCards));
        console.log('✅ Restored cards from localStorage');
      }
    } catch (storageError) {
      console.error('Failed to restore from localStorage:', storageError);
      // Only reset if we truly have no data
      setBalances({
        BTC: 0,
        USD: 0,
        USDC: 0,
        ETH: 0,
        USDT: 0,
        BNB: 0,
        XRP: 0,
        totalUSD: 0
      });
      setWallets([]);
      setCryptoCards([]);
      setTransactions([]);
    }
  }
};

const loadCryptoCards = async () => {
  // Cards will be loaded after user activates them via backend
  setCryptoCards([]);
};

  const loadWallets = async () => {
  try {
    // ✅ PREVENT CONCURRENT CALLS - If already loading, skip this call
    if (isLoadingWallets) {
      console.log('⏭️  loadWallets already running, skipping...');
      return;
    }
    
    setIsLoadingWallets(true);
    console.log('🔄 Loading wallets...');
    
    // Get user's session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.log('❌ No session found');
      return;
    }

    // ✅ FIX: Fetch user role directly (don't rely on state)
    const { data: userData } = await supabase
      .from('users')
      .select('role')
      .eq('id', session.user.id)
      .single();
    
    const userRole = userData?.role;
    console.log('✅ Fetched user role directly:', userRole);  // Debug

    // Load wallets directly from Supabase
    const { data: walletsData, error } = await supabase
      .from('wallets')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: true });

    if (error) throw error;

    console.log('✅ Loaded', walletsData.length, 'manual wallets from Supabase');
    console.log('📍 First wallet addresses:', walletsData[0]?.wallet_addresses);

// Transform wallets for display
    const transformedWallets = walletsData.map(wallet => ({
      id: wallet.id,
      name: wallet.name || 'Unnamed Wallet',
      isCard: false, // Mark as manual wallet (not a card)
      wallet_addresses: wallet.wallet_addresses || {},
      shortAddress: wallet.wallet_addresses?.BTC 
        ? `...${wallet.wallet_addresses.BTC.slice(-8)}` 
        : `...${wallet.id.slice(-4)}`,
      balances: {
        BTC: (wallet.btc_balance || 0).toFixed(8),
        USD: (wallet.usd_balance || 0).toFixed(2),
        USDC: '0.00',
        ETH: '0.00',
        USDT: '0.00',
        BNB: '0.00',
        XRP: '0'
      }
    }));

    
    // Set wallet addresses for receive modal
    if (walletsData[0]?.wallet_addresses) {
      setWalletAddresses(walletsData[0].wallet_addresses);
    }
    
    // Load activated cards from card-wallet endpoint (returns updated balance)
    console.log('🔄 Loading activated cards from card-wallet endpoint...');
    const cardsResponse = await cardAPI.getCards();
    const cards = cardsResponse.success ? (cardsResponse.data?.cards || cardsResponse.cards || []) : [];
    console.log('✅ Loaded', cards.length, 'activated cards with updated balances');
    
    // Transform cards into wallet format with REAL addresses and balances from backend
    const cardWallets = cards.map(card => ({
      id: `card-${card.id}`,
      name: `Card Wallet (${card.maskedCardNumber || (card.cardNumber ? `****${card.cardNumber.slice(-4)}}` : "****")})`,
      isCard: true,
      cardData: card,
      // Use real BTC address from card
      address: card.address || card.btcAddress || card.card_btc_address,
      wallet_addresses: {
        BTC: card.address || card.btcAddress || card.card_btc_address
      },
      shortAddress: card.address ? `...${card.address.slice(-8)}` : `...${card.id.slice(-8)}`,
      balances: {
        // Backend card-wallet endpoint returns 'balance' field
        BTC: (card.balance || card.current_btc_balance || 0).toFixed(8),
        USD: "0.00",
        USDC: "0.00",
        ETH: "0.00",
        USDT: "0.00",
        BNB: "0.00",
        XRP: "0"
      }
    }));
    
    // ✅ Manual HOT_WALLET is already in transformedWallets - no need to create system one!
    // ✅ PREVENT DUPLICATES: Remove any duplicate wallet IDs before combining
    console.log('🔍 Wallets before deduplication:', {
      transformedWallets: transformedWallets.length,
      cardWallets: cardWallets.length
    });
    
    const allWallets = [...transformedWallets, ...cardWallets];
    console.log('🔍 All wallets combined:', allWallets.map(w => ({ id: w.id, name: w.name, btc: w.balances?.BTC })));
    
    const uniqueWallets = [];
    const seenIds = new Set();
    
    for (const wallet of allWallets) {
      if (!seenIds.has(wallet.id)) {
        uniqueWallets.push(wallet);
        seenIds.add(wallet.id);
      } else {
        console.warn('⚠️ Duplicate wallet detected and removed:', { id: wallet.id, name: wallet.name });
      }
    }
    
    const totalWallets = uniqueWallets;
    console.log('✅ Total wallets to display:', totalWallets.length, '(', transformedWallets.length, 'manual +', cardWallets.length, 'cards)');
    setWallets(totalWallets);
    
    // Save wallets to localStorage for persistence
    try {
      localStorage.setItem('globalpay_wallets', JSON.stringify(totalWallets));
      console.log('💾 Wallets saved to localStorage');
    } catch (storageError) {
      console.warn('Failed to save wallets to localStorage:', storageError);
    }
    
  } catch (error) {
    console.error('❌ Error loading wallets:', error);
    
    // Try to restore wallets from localStorage
    try {
      const savedWallets = localStorage.getItem('globalpay_wallets');
      if (savedWallets) {
        setWallets(JSON.parse(savedWallets));
        console.log('✅ Restored wallets from localStorage');
      } else {
        setWallets([]);
      }
    } catch (storageError) {
      console.error('Failed to restore wallets from localStorage:', storageError);
      setWallets([]);
    }
  } finally {
    // ✅ Always reset loading state, even if there's an error
    setIsLoadingWallets(false);
  }
};

  const fetchWalletAddresses = async (walletId) => {
    setLoadingAddresses(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
const response = await fetch(`${API_URL}/wallet/${walletId}/addresses`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      const result = await response.json();
      if (result.success) {
        setWalletAddresses(result.data);
        console.log("✅ Fetched addresses:", result.data);
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
    } finally {
      setLoadingAddresses(false);
    }
  };

  // Reveal Private Key with Security Warning
  const revealPrivateKey = async (walletId, crypto) => {
    setLoadingPrivateKey(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await fetch(`${API_URL}/wallet/${walletId}/reveal-key`, {
        method: 'POST',
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ crypto })
      });

      const result = await response.json();
      
      if (result.success) {
        setRevealedKeys(prev => ({
          ...prev,
          [`${walletId}-${crypto}`]: result.data.privateKey
        }));
        console.warn('🔐 SECURITY: Private key revealed for', crypto);
        return result.data;
      } else {
        throw new Error(result.error || 'Failed to reveal private key');
      }
    } catch (error) {
      console.error('Error revealing private key:', error);
      alert('Failed to reveal private key: ' + error.message);
      return null;
    } finally {
      setLoadingPrivateKey(false);
    }
  };

  const handleRevealKeyClick = (wallet) => {
    setSelectedWalletForKey(wallet);
    setShowSecurityWarning(true);
  };

  const acknowledgeSecurityWarning = async () => {
    setShowSecurityWarning(false);
    setShowPrivateKeyModal(true);
  };

  const toggleKeyVisibility = (keyId) => {
    setVisibleKeys(prev => ({
      ...prev,
      [keyId]: !prev[keyId]
    }));
  };

  const loadTransactions = async () => {
  try {
    const response = await walletAPI.getTransactions();
    if (response.success) {
      setTransactions(response.data.transactions || []);
    }
  } catch (error) {
    console.error('Error loading transactions:', error);
    setTransactions([]);
  }
};
  const calculateExchangeAmount = (amount, fromCurrency, toCurrency) => {
    if (!amount || !fromCurrency || !toCurrency) return '0.00';
    if (!exchangeRates[fromCurrency] || !exchangeRates[toCurrency]) return '0.00';
    
    const fromRate = exchangeRates[fromCurrency];
    const toRate = exchangeRates[toCurrency];
    const result = ((parseFloat(amount) / fromRate) * toRate);
    
    return isNaN(result) ? '0.00' : result.toFixed(2);
  };

  const calculateCryptoConversion = (amount, fromCrypto, toCrypto) => {
    if (!amount || !fromCrypto || !toCrypto || !cryptoPrices[fromCrypto] || !cryptoPrices[toCrypto]) return 0;
    const fromPrice = cryptoPrices[fromCrypto];
    const toPrice = cryptoPrices[toCrypto];
    return ((parseFloat(amount) * fromPrice) / toPrice).toFixed(8);
  };

  const calculateCryptoToCash = (amount, crypto, currency) => {
    if (!amount || !crypto || !currency || !cryptoPrices[crypto] || !exchangeRates[currency]) return 0;
    const cryptoPrice = cryptoPrices[crypto];
    const usdAmount = parseFloat(amount) * cryptoPrice;
    const rate = exchangeRates[currency] || 1;
    return (usdAmount * rate).toFixed(2);
  };

  const formatCurrency = (amount, currencyCode) => {
    const currency = currencies.find(c => c.code === currencyCode);
    return `${currency?.symbol || ''}${amount}`;
  };

  const checkKYCRequired = (amount) => {
    const currentTotal = user?.totalTransactionAmount || 0;
    return (currentTotal + parseFloat(amount)) > 900;
  };

  const checkSpendingLimit = (amount) => {
    const currentTotal = user?.totalTransactionAmount || 0;
    const newTotal = currentTotal + parseFloat(amount);
    
    if (user?.kycLevel === 'minimal' && newTotal > spendingLimit.daily) {
      return { exceeded: true, type: 'daily', limit: spendingLimit.daily };
    }
    if (user?.kycLevel === 'full' && newTotal > spendingLimit.monthly) {
      return { exceeded: true, type: 'monthly', limit: spendingLimit.monthly };
    }
    return { exceeded: false };
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleProfilePictureUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file (JPG, PNG)');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileData({...profileData, profilePicture: e.target.result});
        setUser({...user, profilePicture: e.target.result});
        alert('Profile picture updated successfully!');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleKYCFileUpload = (event, fileType) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file (JPG, PNG)');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setKycData({...kycData, [fileType]: e.target.result});
        alert(`${fileType.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} uploaded successfully!`);
      };
      reader.readAsDataURL(file);
    }
  };

  // 3D Face Capture Handlers with Auto-Detection
  const start3DFaceCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        } 
      });
      setVideoStream(stream);
      setIsCameraActive(true);
      setShow3DFaceModal(true);
      setCaptureStep('front');
      setFaceCaptures({ front: null, left: null, right: null });
    } catch (error) {
      console.error('Camera access error:', error);
      alert('Camera access denied. Please allow camera permission to continue with face verification.');
    }
  };

  const detectFaceInOval = () => {
    const video = document.getElementById('faceVideo');
    if (!video || video.readyState !== video.HAVE_ENOUGH_DATA) return false;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set canvas to video dimensions
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw current video frame
    ctx.drawImage(video, 0, 0);
    
    // Define oval region to match visual guide (30% x 45%)
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const ovalWidth = canvas.width * (ovalDimensions.rx / 50);
    const ovalHeight = canvas.height * (ovalDimensions.ry / 50);
    
    // Sample pixels in oval region to detect face
    const samplePoints = 20;
    let brightPixels = 0;
    let totalSamples = 0;
    
    for (let i = 0; i < samplePoints; i++) {
      const angle = (i / samplePoints) * Math.PI * 2;
      const x = Math.floor(centerX + Math.cos(angle) * ovalWidth * 0.7);
      const y = Math.floor(centerY + Math.sin(angle) * ovalHeight * 0.7);
      
      const pixelData = ctx.getImageData(x, y, 1, 1).data;
      const brightness = (pixelData[0] + pixelData[1] + pixelData[2]) / 3;
      
      // Face detection: look for skin-tone brightness (not too dark, not too bright)
      if (brightness > 60 && brightness < 220) {
        brightPixels++;
      }
      totalSamples++;
    }
    
    // If more than 75% of samples show face-like brightness, consider face detected
    return (brightPixels / totalSamples) > 0.75;
  };

  const captureImage = () => {
    const video = document.getElementById('faceVideo');
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    return canvas.toDataURL('image/jpeg', 0.9);
  };

  const handleAutoCapture = () => {
    const imageData = captureImage();
    
    if (captureStep === 'front') {
      setFaceCaptures(prev => ({ ...prev, front: imageData }));
      setTimeout(() => setCaptureStep('left'), 1000);
    } else if (captureStep === 'left') {
      setFaceCaptures(prev => ({ ...prev, left: imageData }));
      setTimeout(() => setCaptureStep('right'), 1000);
    } else if (captureStep === 'right') {
      setFaceCaptures(prev => ({ ...prev, right: imageData }));
      setTimeout(() => {
        setCaptureStep('complete');
        stopCamera();
      }, 500);
    }
  };

  const stopCamera = () => {
    if (videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
      setVideoStream(null);
    }
    setIsCameraActive(false);
  };

  const save3DFaceCapture = async () => {
    const combinedCapture = {
      front: faceCaptures.front,
      left: faceCaptures.left,
      right: faceCaptures.right,
      timestamp: new Date().toISOString()
    };
    
    setKycData(prev => ({ ...prev, faceCapture: combinedCapture }));
    setShow3DFaceModal(false);
    alert('3D face capture completed successfully!');
  };

  const retake3DCapture = () => {
    setCaptureStep('front');
    setFaceCaptures({ front: null, left: null, right: null });
    start3DFaceCapture();
  };

  const captureSelfieWithID = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        } 
      });
      setSelfieStream(stream);
      setShowSelfieModal(true);
    } catch (error) {
      console.error('Camera access error:', error);
      alert('Camera access denied. Please allow camera permission.');
    }
  };

  const handleSelfieCaptureClick = () => {
    const video = document.getElementById('selfieVideo');
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    const imageData = canvas.toDataURL('image/jpeg', 0.9);
    
    setKycData(prev => ({ ...prev, selfieWithId: imageData }));
    
    if (selfieStream) {
      selfieStream.getTracks().forEach(track => track.stop());
      setSelfieStream(null);
    }
    setShowSelfieModal(false);
    alert('Selfie captured successfully!');
  };

  const closeSelfieModal = () => {
    if (selfieStream) {
      selfieStream.getTracks().forEach(track => track.stop());
      setSelfieStream(null);
    }
    setShowSelfieModal(false);
  };

  const generateAuthCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

 const getFilteredTransactions = () => {
  if (transactionFilter === 'all') return transactions;
  
  // Map filter types to actual database transaction types
  if (transactionFilter === 'sent') {
    return transactions.filter(tx => 
      tx.type === 'sent' || 
      tx.type === 'withdrawal' ||
      tx.type === 'send'
    );
  }
  
  if (transactionFilter === 'received') {
    return transactions.filter(tx => 
      tx.type === 'received' || 
      tx.type === 'deposit' ||
      tx.type === 'card_activation'
    );
  }
  
  if (transactionFilter === 'convert') {
    return transactions.filter(tx => 
      tx.type === 'convert' || 
      tx.type === 'conversion' ||
      tx.type === 'swap'
    );
  }
  
  if (transactionFilter === 'withdraw') {
    return transactions.filter(tx => 
      tx.type === 'withdraw' || 
      tx.type === 'withdrawal'
    );
  }
  
  return transactions.filter(tx => tx.type === transactionFilter);
};

const getTransactionDirection = (tx) => {
  const receivedTypes = ['received', 'deposit', 'card_activation'];
  const sentTypes = ['sent', 'withdrawal', 'send'];
  
  if (receivedTypes.includes(tx.type)) return 'received';
  if (sentTypes.includes(tx.type)) return 'sent';
  return 'neutral';
};

const getTransactionTitle = (tx) => {
  const typeMap = {
    'card_activation': 'Card Activation',
    'withdrawal': 'BTC Withdrawal',
    'deposit': 'BTC Deposit',
    'sent': 'Sent',
    'received': 'Received',
    'convert': 'Conversion',
    'swap': 'Swap'
  };
  return typeMap[tx.type] || tx.type;
};


 const handleCardActivation = async () => {
  if (!activateData.cardNumber || !activateData.pin) {
    alert('Please enter card number and PIN');
    return;
  }

  setLoading(true);
  
  try {
    // Call REAL backend API
    const response = await cardAPI.activateCard(activateData.cardNumber, activateData.pin);

    if (response.success) {
      alert('Card activated successfully! Your wallet has been topped up.');
      setShowActivateCardModal(false);
      setActivateData({ cardNumber: '', pin: '', code: '', generatedCode: '' });
      
      // ✅ Add small delay to ensure database commit completes
      console.log('⏳ Waiting for database to update...');
      await new Promise(resolve => setTimeout(resolve, 1500));  // Wait 1.5 seconds
      
      // Reload REAL data from backend
      console.log('🔄 Reloading all data after card activation...');
      await loadRealDataFromBackend();
      await loadWallets();  // This will fetch cards with updated balances
      await loadTransactions();
      console.log('✅ All data reloaded - new card should now be visible');
      
      if (!user) {
        setShowLandingPage(false);
      }
    }
  } catch (error) {
    console.error('Card activation error:', error);
    alert(`Card activation failed: ${error.message}`);
  } finally {
    setLoading(false);
  }
};
const handleLogout = async () => {
  await supabase.auth.signOut();
  setShowLandingPage(true);
  setUser(null);
  setCryptoCards([]);
  setWallets([]);
  setBalances({});
  setTransactions([]);
  setActiveTab('dashboard');
  alert('You have been logged out successfully');
};

  // PIN Verification Functions - Updated for new PinVerificationModal
  const showPinVerification = (transactionType, action) => {
    setPendingTransaction({
      type: transactionType,
      execute: action
    });
    setShowPinModal(true);
  };

  const handlePinSuccess = () => {
    if (pendingTransaction && pendingTransaction.execute) {
      pendingTransaction.execute();
    }
    setPendingTransaction(null);
  };

  const handlePinCancel = () => {
    setPendingTransaction(null);
    alert('Transaction cancelled');
  };

  // Handle Transaction PIN Setup
  const handleSetTransactionPin = async () => {
    setPinError('');

    // Validate PIN
    if (!pinSetupData.pin || pinSetupData.pin.length !== 4) {
      setPinError('PIN must be exactly 4 digits');
      return;
    }

    if (!/^\d{4}$/.test(pinSetupData.pin)) {
      setPinError('PIN must contain only numbers');
      return;
    }

    if (pinSetupData.pin !== pinSetupData.confirmPin) {
      setPinError('PINs do not match');
      return;
    }

    if (!pinSetupData.password) {
      setPinError('Please enter your account password to verify');
      return;
    }

    setLoading(true);

    try {
      // Verify current password
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: pinSetupData.password
      });

      if (authError) {
        setPinError('Incorrect password');
        setLoading(false);
        return;
      }

      // Save PIN to database
      const { error } = await supabase
        .from('users')
        .update({ transaction_pin: pinSetupData.pin })
        .eq('id', user.id);

      if (error) throw error;

      alert('Transaction PIN set successfully!');
      setShowPinSetup(false);
      setPinSetupData({ pin: '', confirmPin: '', password: '' });
      setPinError('');
    } catch (error) {
      setPinError('Failed to set PIN. Please try again.');
      console.error('PIN setup error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Legacy function for backward compatibility
  const handlePinVerification = () => {
    if (pin === '1234') {
      setShowPinModal(false);
      setPin('');
      if (pendingAction) {
        pendingAction();
        setPendingAction(null);
      }
    } else {
      alert('Invalid PIN. Demo PIN is 1234');
    }
  };
  const handleTransfer = async () => {
    const limitCheck = checkSpendingLimit(transferData.amount);
    if (limitCheck.exceeded) {
      setShowKYCUpgrade(true);
      return;
    }

    if (checkKYCRequired(transferData.amount) && user?.kycLevel === 'minimal') {
      setShowKYCModal(true);
      return;
    }
    
    showPinVerification('transfer', () => {
      setLoading(true);
      setTimeout(() => {
        const convertedAmount = calculateExchangeAmount(transferData.amount, transferData.fromCurrency, transferData.toCurrency);
        alert(`Transfer of ${formatCurrency(transferData.amount, transferData.fromCurrency)} (${formatCurrency(convertedAmount, transferData.toCurrency)}) initiated successfully!`);
        setTransferData({
          recipientEmail: '',
          fromCurrency: '',
          toCurrency: '',
          amount: '',
          message: '',
          paymentMethod: 'crypto',
          selectedCrypto: 'BTC',
          transferType: 'international',
          bankAccount: '',
          cardNumber: '',
          cardName: '',
          expiryDate: '',
          cvv: '',
          zipCode: ''
        });
        setPaymentInterface(null);
        loadTransactions();
        setLoading(false);
      }, 2000);
    });
  };

  // ✅ REPLACE your current handleSendCrypto function (around line 1477) with this:

const handleSendCrypto = async () => {
  try {
    if (!sendCryptoData.sendFrom) {
      alert('Please select a wallet or card to send from');
      return;
    }

    if (!sendCryptoData.recipientAddress || !sendCryptoData.amount) {
      alert('Please enter recipient address and amount');
      return;
    }

    if (parseFloat(sendCryptoData.amount) <= 0) {
      alert('Amount must be greater than 0');
      return;
    }

    const selectedId = sendCryptoData.sendFrom;
    console.log('📤 Sending from:', selectedId);
    
    const selectedWallet = wallets.find(w => {
      const walletKey = w.isCard ? `card-${w.id}` : `wallet-${w.id}`;
      return walletKey === selectedId;
    });
    
    if (!selectedWallet) {
      alert('Selected wallet/card not found. Please refresh and try again.');
      return;
    }
    
    console.log('📍 Found wallet:', selectedWallet.name, 'Type:', selectedWallet.isCard ? 'Card' : 'Manual');
    
    const availableBalance = parseFloat(selectedWallet.balances?.BTC || selectedWallet.btc_balance || 0);
    const amountToSend = parseFloat(sendCryptoData.amount);
    
    console.log('💰 Available:', availableBalance, 'BTC');
    console.log('💸 Want to send:', amountToSend, 'BTC');
    
    if (amountToSend > availableBalance) {
      alert(`Insufficient BTC balance!\n\nAvailable: ${availableBalance.toFixed(8)} BTC\nRequested: ${amountToSend.toFixed(8)} BTC\nShortage: ${(amountToSend - availableBalance).toFixed(8)} BTC`);
      return;
    }

    showPinVerification('send', async () => {
      setLoading(true);
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          throw new Error('Not authenticated. Please log in again.');
        }
        
        const isCardWallet = selectedWallet.isCard === true;
        
        let response;
        let apiUrl;
        let requestBody;
        
        if (isCardWallet) {
          console.log('💳 Sending from CARD wallet');
          const realCardId = selectedWallet.cardData?.id || selectedWallet.id.replace('card-', '');
          console.log('   Card ID:', realCardId);
          
          apiUrl = `${API_URL}/card-wallet/card-${realCardId}/send`;
          requestBody = {
            toAddress: sendCryptoData.recipientAddress,
            amount: amountToSend
          };
          
        } else {
          console.log('👛 Sending from MANUAL wallet');
          console.log('   Wallet ID:', selectedWallet.id);
          console.log('   Wallet Name:', selectedWallet.name);
          
          apiUrl = `${API_URL}/wallet/withdraw`;
          requestBody = {
            walletId: selectedWallet.id,  // ✅ ADD THIS - specify which wallet!
            btcAmount: amountToSend,
            toAddress: sendCryptoData.recipientAddress
          };
        }
        
        console.log('🌐 API URL:', apiUrl);
        console.log('📦 Request:', requestBody);
        
        response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify(requestBody)
        });
        
        console.log('📡 Response status:', response.status);
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to send: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('✅ Send successful:', data);
        
        // INSTANT UI UPDATE
        console.log('🔄 Updating UI optimistically...');
        const newBalance = availableBalance - amountToSend;
        
        const updatedWallets = wallets.map(w => {
          if (w.id === selectedWallet.id) {
            return {
              ...w,
              btc_balance: newBalance,
              balances: {
                ...w.balances,
                BTC: newBalance
              }
            };
          }
          return w;
        });
        
        setWallets(updatedWallets);
        console.log('✅ UI updated! New balance:', newBalance.toFixed(8), 'BTC');
        
        let successMessage = `✅ Successfully sent ${amountToSend.toFixed(8)} BTC!\n\n`;
        successMessage += `From: ${selectedWallet.name}\n`;
        successMessage += `To: ${sendCryptoData.recipientAddress}\n`;
        successMessage += `New Balance: ${newBalance.toFixed(8)} BTC`;
        
        if (data.testMode) {
          successMessage = `🧪 TEST MODE - Transaction Simulated\n\n${successMessage}`;
          successMessage += `\n\nNo real Bitcoin was sent. Balance updated in database.`;
        } else if (data.transaction?.txId) {
          successMessage += `\n\nTransaction ID:\n${data.transaction.txId}`;
          if (data.transaction.explorerUrl) {
            successMessage += `\n\nView on blockchain:\n${data.transaction.explorerUrl}`;
          }
        }
        
        alert(successMessage);
        
        setSendCryptoData({ sendFrom: '', crypto: 'BTC', recipientAddress: '', amount: '', note: '' });
        setShowSendCryptoModal(false);
        
        // Background refresh
        console.log('🔄 Refreshing from backend...');
        setTimeout(async () => {
          await loadRealDataFromBackend();
          await loadTransactions();
          console.log('✅ Background refresh complete');
        }, 1000);
        
      } catch (error) {
        console.error('❌ Send error:', error);
        alert(`Failed to send BTC: ${error.message}`);
      } finally {
        setLoading(false);
      }
    });
  } catch (error) {
    console.error('Error:', error);
    alert(`Error: ${error.message}`);
  }
};


// Handle Send from Individual Wallet (Card or Manual)
const handleSendFromWallet = async () => {
  try {
    // Validate inputs
    if (!walletSendData.recipientAddress || !walletSendData.amount) {
      alert('Please enter recipient address and amount');
      return;
    }

    if (parseFloat(walletSendData.amount) <= 0) {
      alert('Amount must be greater than 0');
      return;
    }

    if (!selectedCardWallet) {
      alert('No wallet selected');
      return;
    }

    showPinVerification('send', async () => {
      setLoading(true);
      
      try {
        console.log('📤 Sending from wallet:', selectedCardWallet);
        console.log('   Amount:', walletSendData.amount, 'BTC');
        console.log('   To:', walletSendData.recipientAddress);
        
        // Get auth session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          throw new Error('Not authenticated. Please log in again.');
        }
        
        // ========================================================================
        // 🔥 KEY FIX: Detect wallet type and call correct endpoint
        // ========================================================================
        
        const isCardWallet = selectedCardWallet.isCard === true;
        
        let response;
        let apiUrl;
        let requestBody;
        
        if (isCardWallet) {
          // ✅ SEND FROM CARD WALLET - Use card-wallet endpoint
          console.log('💳 Detected CARD wallet - using card-wallet endpoint');
          
          // Extract real card ID (remove "card-" prefix or use cardData.id)
          const realCardId = selectedCardWallet.cardData?.id || selectedCardWallet.id.replace('card-', '');
          console.log('   Card ID:', realCardId);
          console.log('   Full wallet ID:', selectedCardWallet.id);
          
          apiUrl = `${API_URL}/card-wallet/card-${realCardId}/send`;
          requestBody = {
            toAddress: walletSendData.recipientAddress,
            amount: parseFloat(walletSendData.amount)
          };
          
        } else {
          // ✅ SEND FROM MANUAL WALLET - Use wallet endpoint with wallet ID
          console.log('👛 Detected MANUAL wallet - using wallet endpoint');
          console.log('   Wallet ID:', selectedCardWallet.id);
          console.log('   Wallet Name:', selectedCardWallet.name);
          
          // Include wallet ID so backend knows which wallet to send from
          apiUrl = `${API_URL}/wallet/withdraw`;
          requestBody = {
            walletId: selectedCardWallet.id,  // ✅ ADD THIS - specify which wallet!
            btcAmount: parseFloat(walletSendData.amount),
            toAddress: walletSendData.recipientAddress
          };
        }
        
        console.log('🌐 API URL:', apiUrl);
        console.log('📦 Request body:', requestBody);
        
        // Make API call
        response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify(requestBody)
        });
        
        console.log('📡 Response status:', response.status);
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to send: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('✅ Send successful:', data);
        
        // Build success message
        let successMessage = `Successfully sent ${walletSendData.amount} ${walletSendData.crypto}!`;
        
        if (data.testMode) {
          successMessage = `🧪 TEST MODE - Transaction Simulated\n\n${successMessage}`;
          successMessage += `\n\nNo real Bitcoin was sent. Balance updated in database.`;
        } else if (data.transaction?.txId) {
          successMessage += `\n\nTransaction ID: ${data.transaction.txId}`;
          if (data.transaction.explorerUrl) {
            successMessage += `\n\nView: ${data.transaction.explorerUrl}`;
          }
        }
        
        if (data.transaction?.remainingBalance !== undefined) {
          successMessage += `\n\nNew balance: ${data.transaction.remainingBalance.toFixed(8)} BTC`;
        }
        
        alert(successMessage);
        
        // Reset form and close modal
        setWalletSendData({ crypto: 'BTC', recipientAddress: '', amount: '', note: '' });
        setShowSendFromCardModal(false);
        setSelectedCardWallet(null);
        
        // Reload data to show updated balances
        console.log('🔄 Reloading wallet data...');
        await loadWallets();
        await loadRealDataFromBackend();  // Update total balance
        if (typeof loadTransactions === 'function') {
          await loadTransactions();
        }
        console.log('✅ Wallet data reloaded');
        
      } catch (error) {
        console.error('❌ Send error:', error);
        alert(`Failed to send crypto: ${error.message}`);
      } finally {
        setLoading(false);
      }
    });
  } catch (error) {
    console.error('Error:', error);
    alert(`Error: ${error.message}`);
  }
};
// Handle Withdraw from Individual Wallet (Card or Manual)
const handleWithdrawFromWallet = async () => {
  try {
    // Validate inputs
    if (!walletWithdrawData.amount) {
      alert('Please enter withdrawal amount');
      return;
    }

    if (parseFloat(walletWithdrawData.amount) <= 0) {
      alert('Amount must be greater than 0');
      return;
    }

    if (!selectedCardWallet) {
      alert('No wallet selected');
      return;
    }

    showPinVerification('withdrawal', async () => {
      setLoading(true);
      
      try {
        // Call the backend API for withdrawal
        alert(`Withdrawal initiated: ${walletWithdrawData.amount} ${walletWithdrawData.crypto}
        
Method: ${walletWithdrawData.method}
        
Processing time: 1-3 business days`);
        
        // Reset form and close modal
        setWalletWithdrawData({ crypto: 'BTC', amount: '', method: 'bank' });
        setShowWithdrawFromCardModal(false);
        setSelectedCardWallet(null);
        
        // Reload data
        await loadTransactions();
        await loadWallets();
        
      } catch (error) {
        console.error('❌ Withdrawal error:', error);
        alert(`Failed to withdraw: ${error.message}`);
      } finally {
        setLoading(false);
      }
    });
  } catch (error) {
    console.error('Error:', error);
    alert(`Error: ${error.message}`);
  }
};

// PIN Verification Functions
const verifyPinBeforeTransaction = (transactionType, transactionFunction) => {
  setPendingTransaction({
    type: transactionType,
    execute: transactionFunction
  });
  setShowPinModal(true);
};

  const handleConvertCrypto = () => {
    showPinVerification('conversion', () => {
      setLoading(true);
      setTimeout(() => {
        const convertedAmount = calculateCryptoConversion(convertCryptoData.amount, convertCryptoData.fromCrypto, convertCryptoData.toCrypto);
        alert(`Converted ${convertCryptoData.amount} ${convertCryptoData.fromCrypto} to ${convertedAmount} ${convertCryptoData.toCrypto}`);
        setConvertCryptoData({ fromCrypto: 'BTC', toCrypto: 'ETH', amount: '' });
        setShowConvertCryptoModal(false);
        loadTransactions();
        loadWallets();
        setLoading(false);
      }, 2000);
    });
  };

  const handleCryptoToCash = () => {
    showPinVerification('cash-out', () => {
      setLoading(true);
      setTimeout(() => {
        const cashAmount = calculateCryptoToCash(cryptoToCashData.amount, cryptoToCashData.crypto, cryptoToCashData.currency);
        alert(`Converting ${cryptoToCashData.amount} ${cryptoToCashData.crypto} to ${formatCurrency(cashAmount, cryptoToCashData.currency)}`);
        setCryptoToCashData({ crypto: 'BTC', amount: '', currency: 'USD', withdrawMethod: 'bank' });
        setShowCryptoToCashModal(false);
        loadTransactions();
        setLoading(false);
      }, 2000);
    });
  };

  const handleWithdraw = async () => {
    showPinVerification('withdrawal', () => {
      setLoading(true);
      setTimeout(() => {
        alert(`Withdrawal of ${withdrawData.amount} ${withdrawData.currency} initiated!`);
        setWithdrawData({ amount: '', method: 'bank', currency: 'USD' });
        loadTransactions();
        setLoading(false);
      }, 2000);
    });
  };

  const handleTopup = async () => {
    const limitCheck = checkSpendingLimit(topupData.amount);
    if (limitCheck.exceeded) {
      setShowKYCUpgrade(true);
      return;
    }

    if (checkKYCRequired(topupData.amount) && user?.kycLevel === 'minimal') {
      setShowKYCModal(true);
      return;
    }
    
    showPinVerification('top-up', () => {
      setLoading(true);
      setTimeout(() => {
        alert(`Top-up of ${topupData.amount} ${topupData.crypto} initiated!`);
        setTopupData({ amount: '', method: 'external', crypto: 'BTC', buyMethod: 'bank', cardAmount: '', paymentMethod: 'card' });
        loadWallets();
        setLoading(false);
      }, 2000);
    });
  };

  const handleCardActivationInApp = async () => {
    showPinVerification('card-activation', () => {
      setLoading(true);
      setTimeout(() => {
        alert('Card activation successful!');
        setCardData({ cardNumber: '', pin: '' });
        loadCryptoCards();
        loadWallets();
        setLoading(false);
      }, 2000);
    });
  };

  const handleAddBankAccount = () => {
    if (!newBankData.bankName || !newBankData.accountNumber || !newBankData.routingNumber) {
      alert('Please fill in all bank account details');
      return;
    }
    
    const newAccount = {
      id: Date.now().toString(),
      bankName: newBankData.bankName,
      accountNumber: `****${newBankData.accountNumber.slice(-4)}`,
      type: newBankData.accountType
    };
    
    setBankAccounts([...bankAccounts, newAccount]);
    setNewBankData({ bankName: '', accountNumber: '', routingNumber: '', accountType: 'checking' });
    setShowAddBankModal(false);
    alert('Bank account added successfully!');
  };

const handleCreateWallet = async () => {
  if (!newWalletData.walletName) {
    alert('Please enter a wallet name');
    return;
  }
  
  setLoading(true);
  try {
    // Call REAL backend API
    const response = await walletAPI.createWallet({
      name: newWalletData.walletName,
      wallet_type: newWalletData.walletType || 'primary'
    });

    if (response.success) {
      alert('Wallet created successfully!');
      setNewWalletData({ walletName: '', walletType: 'standard' });
      setShowCreateWalletModal(false);
      
      // Reload real data from backend
      await loadWallets();
    } else {
      alert(`Failed to create wallet: ${response.error || 'Unknown error'}`);
    }
  } catch (error) {
    console.error('Create wallet error:', error);
    alert(`Failed to create wallet: ${error.message}`);
  } finally {
    setLoading(false);
  }
};
  const handleCardTopUp = () => {
    if (!topupData.cardAmount) {
      alert('Please select or enter an amount');
      return;
    }

    const limitCheck = checkSpendingLimit(topupData.cardAmount);
    if (limitCheck.exceeded) {
      setShowKYCUpgrade(true);
      return;
    }

    if (checkKYCRequired(topupData.cardAmount) && user?.kycLevel === 'minimal') {
      setShowKYCModal(true);
      return;
    }

    showPinVerification('card-topup', () => {
      setLoading(true);
      setTimeout(() => {
        alert(`Card topped up with $${topupData.cardAmount} successfully!`);
        setTopupData({ ...topupData, cardAmount: '' });
        setShowTopUpCardModal(false);
        loadCryptoCards();
        setLoading(false);
      }, 2000);
    });
  };

  const handleCardSelection = (card) => {
    setSelectedCard(card);
  };

  const handleViewCardHistory = () => {
    if (!selectedCard) {
      alert('Please select a card first by clicking on it');
      return;
    }
    setShowCardHistoryModal(true);
  };
  const KYCUpgradeModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-purple-800 rounded-lg p-6 max-w-lg w-full mx-4 max-h-96 overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">Complete KYC Verification</h3>
          <button onClick={() => setShowKYCUpgrade(false)} className="text-purple-300 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <p className="text-purple-300 mb-4">
          Please complete your KYC verification to increase your spending limits and unlock all features.
        </p>
        
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium mb-2">Country</label>
            <select
              value={kycData.country}
              onChange={(e) => setKycData(prev => ({...prev, country: e.target.value}))}
              className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
            >
              {countries.map(country => (
                <option key={country.code} value={country.code}>
                  {country.flag} {country.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Date of Birth</label>
            <input
              type="date"
              value={kycData.dateOfBirth}
              onChange={(e) => setKycData(prev => ({...prev, dateOfBirth: e.target.value}))}
              className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
            />
          </div>

          <div className="space-y-3">
            <h4 className="font-medium">Required Documents</h4>
            
            <div className="bg-purple-700 rounded p-3">
              <div className="flex items-center justify-between mb-2">
                <span>Government ID</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleKYCFileUpload(e, 'governmentId')}
                  className="hidden"
                  id="governmentId"
                />
                <label
                  htmlFor="governmentId"
                  className="bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded text-sm cursor-pointer"
                >
                  {kycData.governmentId ? 'Uploaded' : 'Upload'}
                </label>
              </div>
              <p className="text-xs text-purple-300">
                {countries.find(c => c.code === kycData.country)?.requiresSSN 
                  ? 'Driver\'s License, Passport, or State ID'
                  : 'National ID, Passport, or Driver\'s License'
                }
              </p>
            </div>

            <div className="bg-purple-700 rounded p-3">
              <div className="flex items-center justify-between mb-2">
                <span>3D Face Capture</span>
                <button
                  onClick={start3DFaceCapture}
                  className={`px-3 py-1 rounded text-sm ${
                    kycData.faceCapture 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {kycData.faceCapture ? 'Captured ✓' : 'Capture'}
                </button>
              </div>
              <p className="text-xs text-purple-300">Live face verification (front, left, right)</p>
            </div>

            <div className="bg-purple-700 rounded p-3">
              <div className="flex items-center justify-between mb-2">
                <span>Selfie with ID</span>
                <button
                  onClick={captureSelfieWithID}
                  className={`px-3 py-1 rounded text-sm ${
                    kycData.selfieWithId 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {kycData.selfieWithId ? 'Captured ✓' : 'Capture'}
                </button>
              </div>
              <p className="text-xs text-purple-300">Photo of you holding your ID</p>
            </div>
          </div>

          <div className="bg-blue-900 bg-opacity-50 rounded p-3">
            <h4 className="font-medium mb-2 text-blue-300">After Verification:</h4>
            <ul className="text-sm space-y-1">
              <li>• Daily limit: $5,000</li>
              <li>• Monthly limit: $50,000</li>
              <li>• Priority support</li>
              <li>• All features unlocked</li>
            </ul>
          </div>
        </div>
        
        <div className="flex space-x-3">
          <button
            onClick={() => setShowKYCUpgrade(false)}
            className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded"
          >
            Maybe Later
          </button>
          <button
            onClick={() => {
              setShowKYCUpgrade(false);
              alert('KYC verification submitted for review!');
            }}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded"
          >
            Submit Verification
          </button>
        </div>
      </div>
    </div>
  );
  const AddBankAccountModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-purple-800 rounded-lg p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">Add Bank Account</h3>
          <button onClick={() => setShowAddBankModal(false)} className="text-purple-300 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Bank Name</label>
            <input
              type="text"
              value={newBankData.bankName}
              onChange={(e) => setNewBankData({...newBankData, bankName: e.target.value})}
              className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
              placeholder="Chase Bank"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Account Number</label>
            <input
              type="text"
              value={newBankData.accountNumber}
              onChange={(e) => setNewBankData({...newBankData, accountNumber: e.target.value})}
              className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
              placeholder="1234567890"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Routing Number</label>
            <input
              type="text"
              value={newBankData.routingNumber}
              onChange={(e) => setNewBankData({...newBankData, routingNumber: e.target.value})}
              className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
              placeholder="021000021"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Account Type</label>
            <select
              value={newBankData.accountType}
              onChange={(e) => setNewBankData({...newBankData, accountType: e.target.value})}
              className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
            >
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
            </select>
          </div>

          <button
            onClick={handleAddBankAccount}
            className="w-full bg-blue-600 hover:bg-blue-700 py-3 rounded-lg font-medium transition-colors"
          >
            Add Bank Account
          </button>
        </div>
      </div>
    </div>
  );
  const CreateWalletModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-purple-800 rounded-lg p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">Create New Wallet</h3>
          <button onClick={() => setShowCreateWalletModal(false)} className="text-purple-300 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Wallet Name</label>
            <input
  key="wallet-name-input"  // Add this line
  type="text"
  value={newWalletData.walletName}
  onChange={(e) => setNewWalletData({...newWalletData, walletName: e.target.value})}
  className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
  placeholder="My Trading Wallet"
  autoFocus
/>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Wallet Type</label>
            <select
              value={newWalletData.walletType}
              onChange={(e) => setNewWalletData({...newWalletData, walletType: e.target.value})}
              className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
            >
              <option value="standard">Standard Wallet</option>
              <option value="savings">Savings Wallet</option>
              <option value="trading">Trading Wallet</option>
            </select>
          </div>

          <div className="bg-blue-900 bg-opacity-40 rounded-lg p-3 border border-blue-600">
            <p className="text-sm">
              <strong>Note:</strong> Your wallet will support all available cryptocurrencies (BTC, ETH, USDC, USDT, BNB, XRP).
            </p>
          </div>

          <button
            onClick={handleCreateWallet}
            className="w-full bg-green-600 hover:bg-green-700 py-3 rounded-lg font-medium transition-colors"
          >
            Create Wallet
          </button>
        </div>
      </div>
    </div>
  );
  const TopUpCardModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-purple-800 rounded-lg p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">Top Up Card</h3>
          <button onClick={() => setShowTopUpCardModal(false)} className="text-purple-300 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Select Amount</label>
            <div className="grid grid-cols-2 gap-3 mb-3">
              {['50', '100', '200', '500'].map((amount) => (
                <button
                  key={amount}
                  onClick={() => setTopupData({...topupData, cardAmount: amount})}
                  className={`p-4 rounded-lg border-2 transition-colors ${
                    topupData.cardAmount === amount
                      ? 'border-green-500 bg-green-600 bg-opacity-20'
                      : 'border-purple-600 bg-purple-700 bg-opacity-30'
                  }`}
                >
                  <p className="text-2xl font-bold">${amount}</p>
                </button>
              ))}
            </div>
            <input
              type="number"
              value={topupData.cardAmount}
              onChange={(e) => setTopupData({...topupData, cardAmount: e.target.value})}
              className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
              placeholder="Or enter custom amount"
            />
          </div>

        <div>
            <label className="block text-sm font-medium mb-2">Payment Method</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setTopupData({...topupData, paymentMethod: 'card'})}
                className={`p-4 rounded-lg border-2 transition-colors ${
                  topupData.paymentMethod === 'card'
                    ? 'border-blue-500 bg-blue-600 bg-opacity-20'
                    : 'border-purple-600 bg-purple-700 bg-opacity-30'
                }`}
              >
                <CreditCard className="mx-auto mb-2" size={24} />
                <p className="font-bold text-sm">Debit/Credit</p>
              </button>
              <button
                onClick={() => setTopupData({...topupData, paymentMethod: 'bank'})}
                className={`p-4 rounded-lg border-2 transition-colors ${
                  topupData.paymentMethod === 'bank'
                    ? 'border-green-500 bg-green-600 bg-opacity-20'
                    : 'border-purple-600 bg-purple-700 bg-opacity-30'
                }`}
              >
                <Building className="mx-auto mb-2" size={24} />
                <p className="font-bold text-sm">Bank Account</p>
              </button>
            </div>
          </div>

          <button
            onClick={handleCardTopUp}
            disabled={loading || !topupData.cardAmount}
            className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed py-3 rounded-lg font-medium transition-colors"
          >
            {loading ? 'Processing...' : 'Top Up Card'}
          </button>
        </div>
      </div>
    </div>
  );
  const CardHistoryModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-purple-800 rounded-lg p-6 max-w-2xl w-full max-h-96 overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">Card Transaction History</h3>
          <button onClick={() => setShowCardHistoryModal(false)} className="text-purple-300 hover:text-white">
            <X size={24} />
          </button>
        </div>

        {selectedCard ? (
          <>
            <div className="bg-purple-700 rounded-lg p-4 mb-4">
              <p className="text-sm text-purple-300">Card Number</p>
              <p className="text-xl font-bold font-mono">{selectedCard.maskedCardNumber}</p>
              <div className="flex justify-between mt-2">
                <div>
                  <p className="text-xs text-purple-300">Balance</p>
                  <p className="font-bold">${selectedCard.valueUSD}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-purple-300">Token ID</p>
                  <p className="font-bold">{selectedCard.tokenId}</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {selectedCard.transactions && selectedCard.transactions.length > 0 ? (
                selectedCard.transactions.map((tx) => (
                  <div key={tx.id} className="bg-purple-700 bg-opacity-50 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-bold capitalize">{tx.type}</p>
                        {tx.merchant && <p className="text-sm text-purple-300">{tx.merchant}</p>}
                        <p className="text-xs text-purple-400">{new Date(tx.date).toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${tx.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {tx.amount > 0 ? '+' : ''}${Math.abs(tx.amount)}
                        </p>
                        <span className={`text-xs px-2 py-1 rounded ${
                          tx.status === 'confirmed' ? 'bg-green-600' : 'bg-yellow-600'
                        }`}>
                          {tx.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Clock className="text-purple-500 mx-auto mb-3" size={48} />
                  <p className="text-purple-300">No transactions yet</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <p className="text-purple-300 text-center py-8">No card selected</p>
        )}
      </div>
    </div>
  );
  const LandingPage = () => (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white">
      <header className="bg-purple-900/50 backdrop-blur-sm border-b border-purple-700 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img 
                src="/zanors-logo-full.png" 
                alt="Zanors Logo" 
                className="h-16 md:h-20 mix-blend-normal" 
                style={{ backgroundColor: 'transparent' }}
              />
            </Link>

            <div className="flex items-center space-x-3">
              <Link 
                to="/shop"
                className="text-orange-400 hover:text-orange-300 font-semibold text-sm md:text-base transition-colors flex items-center gap-2"
              >
                <ShoppingCart size={18} />
                Shop Cards
              </Link>
              <button 
                onClick={() => setShowSignupModal(true)}
                className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg font-medium text-sm md:text-base transition-colors"
              >
                Sign Up
              </button>
              <button 
                onClick={() => setShowLoginModal(true)}
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg font-medium text-sm md:text-base transition-colors"
              >
                Login
              </button>
              <button 
                onClick={() => setShowActivateCardModal(true)}
                className="bg-orange-600 hover:bg-orange-700 px-4 py-2 rounded-lg font-medium text-sm md:text-base transition-colors"
              >
                Activate Card
              </button>
              
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 hover:bg-purple-700 rounded-lg transition-colors"
                aria-label="Menu"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {isMenuOpen && (
          <div className="bg-purple-800 border-t border-purple-700 max-h-96 overflow-y-auto">
            <div className="container mx-auto px-4 py-4">
              <Link
                to="/shop"
                onClick={() => setIsMenuOpen(false)}
                style={{
                  background: 'linear-gradient(90deg, rgba(212,168,50,0.2) 0%, rgba(245,166,35,0.2) 100%)',
                  borderLeft: '3px solid #D4A832'
                }}
                className="w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center justify-between hover:bg-purple-700/50 mb-4"
              >
                <div className="flex items-center space-x-3">
                  <ShoppingCart size={20} className="text-orange-500" />
                  <div>
                    <div className="font-semibold text-orange-400">Shop Crypto Cards</div>
                    <div className="text-xs text-purple-300">$50, $100, $200, $500 • Instant delivery</div>
                  </div>
                </div>
              </Link>

              <div className="border-t border-purple-600 my-2"></div>

              {Object.entries(menuItems).map(([category, items]) => (
                <div key={category} className="mb-2">
                  <button
                    onClick={() => toggleSubmenu(category)}
                    className="w-full flex items-center justify-between p-3 hover:bg-purple-700 rounded-lg transition-colors"
                  >
                    <span className="font-semibold text-lg">{category}</span>
                    <ChevronDown 
                      className={`w-5 h-5 transition-transform ${
                        expandedMenu === category ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  
                  {expandedMenu === category && (
                    <div className="ml-4 mt-2 space-y-1 bg-purple-900/50 rounded-lg p-2">
                      {items.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          className="block p-3 hover:bg-purple-700 rounded-lg transition-colors text-gray-200 hover:text-white"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </header>

      <div 
        className="fixed right-0 top-1/2 transform -translate-y-1/2 z-40 cursor-pointer"
        onMouseEnter={() => setShowQRCode(true)}
        onMouseLeave={() => setShowQRCode(false)}
      >
        <div className={`transition-all duration-300 ${showQRCode ? 'w-48' : 'w-16'} bg-purple-800 border-l-4 border-green-400 rounded-l-lg shadow-lg`}>
          {!showQRCode ? (
            <div className="p-4">
              <div className="w-8 h-8 bg-white rounded grid grid-cols-2 gap-0.5 p-1">
                <div className="bg-black"></div>
                <div className="bg-white"></div>
                <div className="bg-white"></div>
                <div className="bg-black"></div>
              </div>
            </div>
          ) : (
            <div className="p-4">
              <h3 className="text-sm font-bold mb-2 text-center">Get the Zanors App</h3>
              <div className="bg-white p-2 rounded mb-2">
                <div className="grid grid-cols-8 gap-0.5">
                  {[...Array(64)].map((_, i) => (
                    <div 
                      key={i} 
                      className={`aspect-square ${Math.random() > 0.5 ? 'bg-black' : 'bg-white'}`}
                    ></div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <a 
                  href="https://apps.apple.com/app/globalpay" 
                  className="block text-center bg-gray-900 hover:bg-gray-800 px-2 py-1 rounded text-xs transition-colors"
                >
                  App Store
                </a>
                <a 
                  href="https://play.google.com/store/apps/details?id=com.globalpay.app" 
                  className="block text-center bg-gray-900 hover:bg-gray-800 px-2 py-1 rounded text-xs transition-colors"
                >
                  Google Play
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-10">

          {/* Wide descriptive eyebrow — 2 lines, touches on all 3 products */}
          <div className="flex items-start gap-4 mb-10 w-full max-w-7xl mx-auto leading-relaxed text-left rounded-2xl px-12 py-7 text-xl font-medium text-white bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-900 border border-slate-600 shadow-2xl">
            <span className="w-3 h-3 bg-orange-500 rounded-full flex-shrink-0 mt-2"></span>
            <span>
              Revolutionary blockchain-powered infrastructure for international money transfers, automated batch payroll and enterprise payments — secured with bank-grade encryption, smart contract rails and real-time settlement that guarantees every dollar arrives instantly, transparently and at a fraction of traditional costs.
              Bringing cryptocurrency and borderless financial access to the 1.4 billion unbanked globally, through crypto gift cards available at 200,000+ stores.
            </span>
          </div>

          {/* Main headline */}
          <h1 className="text-6xl md:text-8xl font-extrabold mb-8 leading-tight tracking-tight">
            <span className="block text-white">The Financial Platform</span>
            <span className="block bg-gradient-to-r from-orange-400 via-yellow-300 to-orange-400 bg-clip-text text-transparent">
              Built for the World.
            </span>
          </h1>

          <p className="text-2xl md:text-3xl text-purple-200 mb-5 max-w-4xl mx-auto leading-relaxed font-light">
            Three products. One platform. A new financial infrastructure that moves money
            <strong className="text-white font-semibold"> faster, cheaper, and further</strong> than anything that came before it.
          </p>
          <p className="text-xl text-purple-300 mb-14 max-w-3xl mx-auto leading-relaxed">
            Zanors replaces expensive wire transfers, inaccessible Bitcoin ATMs, and manual payroll &mdash;
            connecting individuals, businesses, and the unbanked to a borderless economy.
          </p>

          {/* 3 product pills */}
          <div className="flex flex-wrap justify-center gap-4 mb-14">
            {[
              { label: "Crypto Gift Cards",        sub: "200,000+ stores nationwide",     color: "from-orange-500 to-amber-500" },
              { label: "Batch Payroll & Payments", sub: "Pay 1,000 people in one click",  color: "from-blue-500 to-indigo-600" },
              { label: "Global Money Transfers",   sub: "Blockchain rails - 95% cheaper", color: "from-emerald-500 to-teal-600" },
            ].map(({ label, sub, color }) => (
              <div key={label} className={`bg-gradient-to-r ${color} rounded-2xl px-8 py-5 shadow-lg`}>
                <div className="font-bold text-white text-xl leading-tight">{label}</div>
                <div className="text-white/80 text-base mt-1">{sub}</div>
              </div>
            ))}
          </div>

          {/* CTA buttons */}
          <div className="flex flex-wrap justify-center gap-5 mb-8">
            <button
              onClick={() => setShowSignupModal(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white px-12 py-5 rounded-2xl text-xl font-bold shadow-xl transition-colors"
            >
              Create Free Account
            </button>
            <button
              onClick={() => setShowLoginModal(true)}
              className="bg-white/15 hover:bg-white/25 border border-white/30 text-white px-12 py-5 rounded-2xl text-xl font-bold backdrop-blur-sm transition-colors"
            >
              Login
            </button>
          </div>

          <button
            onClick={() => setShowActivateCardModal(true)}
            className="text-orange-300 hover:text-orange-200 text-base font-semibold underline underline-offset-4 transition-colors"
          >
            Have a Crypto Gift Card? Activate Here
          </button>

          {/* Trust strip */}
          <div className="flex flex-wrap justify-center gap-10 mt-14 pt-10 border-t border-white/10">
            {[
              ["$0",    "Transfer fee on domestic sends"],
              ["180+",  "Countries supported"],
              ["200K+", "Retail store locations"],
              ["< 30s", "Average settlement time"],
              ["95%",   "Cheaper than wire transfers"],
            ].map(([stat, label]) => (
              <div key={stat} className="text-center">
                <div className="text-4xl font-extrabold text-white">{stat}</div>
                <div className="text-sm text-purple-400 mt-1 max-w-[110px] leading-snug">{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* 3 product cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {[
            {
              iconBg: "bg-orange-500/20 border-orange-500/30",
              iconText: "text-orange-400",
              iconLabel: "BTC",
              title: "Bitcoin Gift Cards at Your Corner Store",
              body: "Forget hunting for a Bitcoin ATM charging 15% fees. Our prepaid BTC Gift Cards are live at 200,000+ US retailers including Walmart, CVS, Walgreens and Target, plus every major online marketplace. Instant activation. Zero hidden fees. Bitcoin for everyone.",
              tag: "200,000+ stores - $0 hidden fees",
              tagColor: "text-orange-300 bg-orange-500/10 border-orange-500/20",
            },
            {
              iconBg: "bg-blue-500/20 border-blue-500/30",
              iconText: "text-blue-400",
              iconLabel: "PAY",
              title: "Enterprise Batch Payments & Automated Payroll",
              body: "Pay your entire workforce, contractor network, or vendor list in a single click. Schedule recurring payrolls, import via CSV, and settle via blockchain rails domestically and internationally. Cut payroll overhead by up to 95% with real-time audit trails.",
              tag: "CSV import - Recurring schedules - 95% cost savings",
              tagColor: "text-blue-300 bg-blue-500/10 border-blue-500/20",
            },
            {
              iconBg: "bg-emerald-500/20 border-emerald-500/30",
              iconText: "text-emerald-400",
              iconLabel: "SEND",
              title: "The Most Affordable Money Transfer on Earth",
              body: "Traditional remittance takes 3-5 days and charges 5-8%. Zanors settles in under 30 seconds on blockchain rails with transparent flat fees, delivering more money to the people who need it most across 180+ countries, including the 1.4 billion unbanked.",
              tag: "180+ countries - Under 30s - Flat fee transparency",
              tagColor: "text-emerald-300 bg-emerald-500/10 border-emerald-500/20",
            },
          ].map(({ iconBg, iconText, iconLabel, title, body, tag, tagColor }) => (
            <div key={title} className="bg-purple-900/50 border border-purple-700/60 rounded-3xl p-9 backdrop-blur-sm flex flex-col">
              <div className={`w-16 h-16 rounded-2xl border flex items-center justify-center mb-6 ${iconBg}`}>
                <span className={`text-sm font-extrabold tracking-wider ${iconText}`}>{iconLabel}</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 leading-snug">{title}</h3>
              <p className="text-purple-200 text-base leading-relaxed mb-6 flex-1">{body}</p>
              <div className={`inline-flex items-center text-sm font-semibold px-4 py-2 rounded-full border w-fit ${tagColor}`}>
                {tag}
              </div>
            </div>
          ))}
        </div>

        {/* Why Zanors wins */}
        <div className="bg-gradient-to-r from-purple-900/80 via-indigo-900/80 to-purple-900/80 border border-purple-700/50 rounded-3xl p-12 mb-16 backdrop-blur-sm">
          <div className="text-center mb-12">
            <p className="text-orange-400 text-sm font-bold uppercase tracking-widest mb-3">Our Competitive Edge</p>
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4">Why Zanors Has the Global Edge</h2>
            <p className="text-purple-300 text-xl mt-3 max-w-2xl mx-auto leading-relaxed">We are not another money transfer app. We are building the financial rails that power the next billion users.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { stat: "3-in-1", label: "Platform",         desc: "Gift cards plus payroll plus remittance in one unified platform. Competitors offer one, we offer all three." },
              { stat: "$0",     label: "Base Fee",          desc: "Our blockchain-native architecture eliminates the correspondent banking overhead that costs competitors dearly." },
              { stat: "1.4B",   label: "Unbanked Market",   desc: "Our crypto rails reach recipients without bank accounts, unlocking a market traditional fintechs cannot serve." },
              { stat: "10x",    label: "Faster Settlement", desc: "Smart contract automation delivers sub-30 second settlement vs 3-5 business days for legacy wire transfers." },
            ].map(({ stat, label, desc }) => (
              <div key={label} className="bg-white/5 border border-white/10 rounded-2xl p-7">
                <div className="text-5xl font-extrabold text-orange-400 mb-2">{stat}</div>
                <div className="text-lg font-bold text-white mb-3">{label}</div>
                <p className="text-sm text-purple-300 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
        {/* ===== BTC Gift Card vs ATM Ad Section ===== */}
<BTCGiftCardVsATMSection onGetStarted={() => setShowSignupModal(true)} />
{/* =========================================== */}

        {/* ========== CURRENCY CONVERTER ========== */}
<CurrencyConverterWidget 
  onGetStarted={() => setShowSignupModal(true)} 
  onActivateCard={() => setShowActivateCardModal(true)}
/>
{/* ======================================== */}


        <div className="mb-16">
          <h2 className="text-4xl font-bold text-center mb-12">
            <Shield className="inline mr-3" size={40} />
            Bank-Grade Security & Full Compliance
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm border border-purple-600">
              <Lock className="text-green-400 mb-4" size={32} />
              <h3 className="text-lg font-bold mb-2">AES-256 Encryption</h3>
              <p className="text-purple-200 text-sm">
                Military-grade encryption protects all data and transactions
              </p>
            </div>

            <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm border border-purple-600">
              <Database className="text-blue-400 mb-4" size={32} />
              <h3 className="text-lg font-bold mb-2">Blockchain Security</h3>
              <p className="text-purple-200 text-sm">
                Immutable ledger technology ensures transaction integrity
              </p>
            </div>

            <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm border border-purple-600">
              <Layers className="text-yellow-400 mb-4" size={32} />
              <h3 className="text-lg font-bold mb-2">Smart Contracts</h3>
              <p className="text-purple-200 text-sm">
                Automated execution eliminates human error and fraud
              </p>
            </div>

            <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm border border-purple-600">
              <CheckCircle className="text-green-400 mb-4" size={32} />
              <h3 className="text-lg font-bold mb-2">Multi-Factor Auth</h3>
              <p className="text-purple-200 text-sm">
                Biometric verification and 2FA protect your account
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-900 to-purple-900 rounded-lg p-8 border border-blue-500">
            <h3 className="text-2xl font-bold mb-6 text-center">
              <Award className="inline mr-2" size={28} />
              Fully Licensed & Regulated Financial Platform
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <Building className="text-blue-400 mx-auto mb-3" size={32} />
                <h4 className="font-bold mb-2">Money Transmission License</h4>
                <p className="text-sm text-blue-200">Licensed in all 50 US states and territories coming soon</p>
              </div>
              <div className="text-center">
                <FileText className="text-green-400 mx-auto mb-3" size={32} />
                <h4 className="font-bold mb-2">FinCEN Registration </h4>
                <p className="text-sm text-green-200">US Department of Treasury compliance - on process</p>
              </div>
              <div className="text-center">
                <Globe className="text-purple-400 mx-auto mb-3" size={32} />
                <h4 className="font-bold mb-2">International Licenses</h4>
                <p className="text-sm text-purple-200">Central bank authorized in 45+ countries - on process</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="bg-purple-800 bg-opacity-50 rounded-lg p-8 backdrop-blur-sm transform hover:scale-105 transition-all duration-300">
            <Zap className="text-yellow-400 text-5xl mb-4" />
            <h3 className="text-2xl font-bold mb-4">Lightning Fast</h3>
            <p className="text-purple-200">
              Transfers complete in under 30 seconds using cutting-edge blockchain technology
              with smart contract automation
            </p>
          </div>

          <div className="bg-purple-800 bg-opacity-50 rounded-lg p-8 backdrop-blur-sm transform hover:scale-105 transition-all duration-300">
            <DollarSign className="text-green-400 text-5xl mb-4" />
            <h3 className="text-2xl font-bold mb-4">Ultra Low Fees</h3>
            <p className="text-purple-200">
              Flat $0 fee per transaction - save 99.9% vs traditional money transfer services
              with full transparency
            </p>
          </div>

          <div className="bg-purple-800 bg-opacity-50 rounded-lg p-8 backdrop-blur-sm transform hover:scale-105 transition-all duration-300">
            <Globe className="text-blue-400 text-5xl mb-4" />
            <h3 className="text-2xl font-bold mb-4">Global Reach</h3>
            <p className="text-purple-200">
              Send to 180+ countries including USA, China, Switzerland, Canada, Brazil, Nigeria, Kenya, Mexico
              and more with local banking partnerships
            </p>
          </div>
        </div>
      </div>

      <footer className="bg-black bg-opacity-50 mt-16 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="font-bold text-lg mb-4">Services</h4>
              <ul className="space-y-2 text-purple-300">
                {menuItems.Services.map(item => (
                  <li key={item.name}>
                    <Link to={item.path} className="hover:text-white transition-colors">{item.name}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Company</h4>
              <ul className="space-y-2 text-purple-300">
                {menuItems.Company.map(item => (
                  <li key={item.name}>
                    <Link to={item.path} className="hover:text-white transition-colors">{item.name}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Support</h4>
              <ul className="space-y-2 text-purple-300">
                {menuItems.Support.map(item => (
                  <li key={item.name}>
                    <Link to={item.path} className="hover:text-white transition-colors">{item.name}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Legal</h4>
              <ul className="space-y-2 text-purple-300">
                {menuItems.Legal.map(item => (
                  <li key={item.name}>
                    <Link to={item.path} className="hover:text-white transition-colors">{item.name}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="border-t border-purple-700 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex items-center space-x-3 mb-4 md:mb-0">
                <div className="w-14 h-14 rounded-full flex items-center justify-center p-2 border border-purple-600 shadow-lg">
                  <img src="/zanors-icon.png" alt="Zanors Icon" className="w-full h-full object-contain" />
                </div>
                <div>
                  <span className="text-white font-bold">Zanors</span>
                  <p className="text-xs text-purple-400">Zanors is a financial technology platform. Zanors is not an FDIC-insured Bank or depositary</p>
                  <p className="text-xs text-purple-400">taking institution. In the USA, banking services for the placement of deposits where the customers'</p>
                  <p className="text-xs text-purple-400">funds may be placed are provided by BankProv, Member FDIC. Deposit insurance coverage only</p>
                  <p className="text-xs text-purple-400">protects against the failure of an FDIC insured depository institution. Crypto assets and</p>
                  <p className="text-xs text-purple-400">non-bank digital wallet are not insured by the FDIC, are not deposits, and may lose value.</p>
                </div>
              </div>
              
              <div className="text-center md:text-right">
                <p className="text-purple-300 text-sm">
                  © 2026 Zanors Inc. All rights reserved.
                </p>
                <p className="text-purple-400 text-xs mt-1">
                  Licensed by FinCEN • Regulated by NMLS • Member FDIC
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );

  if (showLandingPage) {
    return (
      <>
        <LandingPage />
       <SignupModal 
  showSignupModal={showSignupModal}
  setShowSignupModal={setShowSignupModal}
  signupData={signupData}
  setSignupData={setSignupData}
  countries={countries}
  setShowLandingPage={setShowLandingPage}
/>
        <LoginModal 
          showLoginModal={showLoginModal}
          setShowLoginModal={setShowLoginModal}
          loginStep={loginStep}
          setLoginStep={setLoginStep}
          loginData={loginData}
          setLoginData={setLoginData}
          generateAuthCode={generateAuthCode}
          setShowLandingPage={setShowLandingPage}
        />
     <ActivateCardModal 
  showActivateCardModal={showActivateCardModal}
  setShowActivateCardModal={setShowActivateCardModal}
  activateData={activateData}
  setActivateData={setActivateData}
  onSuccess={async () => {
    // Reload wallet data after successful activation
    await loadRealDataFromBackend();
    await loadWallets();
    await loadTransactions();
    if (!user) {
      setShowLandingPage(false);
    }
  }}
/>
        {showKYCUpgrade && <KYCUpgradeModal />}
      </>
    );
  }
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white">
      <header className="bg-purple-900 bg-opacity-50 backdrop-blur-sm border-b border-purple-700 sticky top-0 z-40">
  <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
    <div className="flex justify-between items-center">
      <div className="flex items-center space-x-2 md:space-x-4">
        <img 
          src="/zanors-logo-full.png" 
          alt="Zanors Logo" 
          className="h-12 md:h-16 mix-blend-normal" 
          style={{ backgroundColor: 'transparent' }}
        />
        <div>
          <p className="text-xs text-purple-300 hidden md:block">Welcome back, {user?.firstName}!</p>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        {/* Balance - Hidden on small screens */}
        <div className="text-right hidden lg:block">
          <p className="text-sm text-purple-300">Total Balance</p>
          <div className="flex items-center space-x-2">
            <p className="text-2xl font-bold">
              {showBalances ? `$${balances.totalUSD?.toFixed(2) || '0.00'}` : '****'}
            </p>
            <button onClick={() => setShowBalances(!showBalances)}>
              {showBalances ? <Eye size={20} /> : <EyeOff size={20} />}
            </button>
          </div>
        </div>

        {/* Profile Button */}
        <button 
          onClick={() => setShowProfileModal(true)}
          className="bg-purple-700 hover:bg-purple-600 p-2 rounded-full transition-colors relative overflow-hidden"
        >
          {user?.profilePicture ? (
            <img 
              src={user.profilePicture} 
              alt="Profile" 
              className="w-8 h-8 md:w-10 md:h-10 rounded-full object-cover"
            />
          ) : (
            <User size={20} className="md:w-6 md:h-6" />
          )}
        </button>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="p-2 hover:bg-purple-700 rounded-lg transition-colors"
          aria-label="Menu"
        >
          {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>

        {/* Desktop Logout - Hidden on mobile */}
        <button 
          onClick={handleLogout}
          className="hidden md:flex bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg items-center space-x-2 transition-colors"
        >
          <LogOut size={20} />
          <span className="hidden lg:inline">Logout</span>
        </button>
      </div>
    </div>

    {/* Mobile Dropdown Menu */}
    {isMenuOpen && (
      <div className="mt-4 bg-purple-800 rounded-lg p-3 border border-purple-600">
        {/* Balance on Mobile */}
        <div className="lg:hidden mb-3 pb-3 border-b border-purple-600">
          <p className="text-sm text-purple-300">Total Balance</p>
          <div className="flex items-center justify-between">
            <p className="text-2xl font-bold">
              {showBalances ? `$${balances.totalUSD?.toFixed(2) || '0.00'}` : '****'}
            </p>
            <button onClick={() => setShowBalances(!showBalances)}>
              {showBalances ? <Eye size={20} /> : <EyeOff size={20} />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          {/* Profile Settings */}
          <button
            onClick={() => {
              setShowProfileModal(true);
              setIsMenuOpen(false);
            }}
            className="w-full text-left px-4 py-3 hover:bg-purple-700 rounded-lg transition-colors flex items-center space-x-3"
          >
            <User size={20} />
            <span>Profile Settings</span>
          </button>
          
          {/* KYC Verification */}
          <button
            onClick={() => {
              setShowKYCUpgrade(true);
              setIsMenuOpen(false);
            }}
            className="w-full text-left px-4 py-3 hover:bg-purple-700 rounded-lg transition-colors flex items-center space-x-3"
          >
            <Shield size={20} />
            <span>KYC Verification</span>
          </button>

          {/* ⭐ SHOP CRYPTO CARDS ⭐ */}
          <div className="border-t border-purple-600 my-2"></div>
          
          <Link
            to="/shop"
            onClick={() => setIsMenuOpen(false)}
            style={{
              background: 'linear-gradient(90deg, rgba(212,168,50,0.2) 0%, rgba(245,166,35,0.2) 100%)',
              borderLeft: '3px solid #D4A832'
            }}
            className="w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center justify-between hover:bg-purple-700/50"
          >
            <div className="flex items-center space-x-3">
              <ShoppingCart size={20} className="text-orange-500" />
              <div>
                <div className="font-semibold text-orange-400">Shop Crypto Cards</div>
                <div className="text-xs text-purple-300">$50, $100, $200, $500 • Instant delivery</div>
              </div>
            </div>
          </Link>

          {/* Services Submenu */}
          <div className="border-t border-purple-600 my-2"></div>
          
          <button
            onClick={() => toggleSubmenu('Services')}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-purple-700 rounded-lg transition-colors"
          >
            <span className="font-semibold">Services</span>
            <ChevronDown 
              className={`w-5 h-5 transition-transform ${
                expandedMenu === 'Services' ? 'rotate-180' : ''
              }`}
            />
          </button>
          
          {expandedMenu === 'Services' && (
            <div className="ml-4 space-y-1">
              {menuItems.Services.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className="block px-4 py-2 hover:bg-purple-700 rounded-lg transition-colors text-sm"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          )}

          {/* Company Submenu */}
          <button
            onClick={() => toggleSubmenu('Company')}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-purple-700 rounded-lg transition-colors"
          >
            <span className="font-semibold">Company</span>
            <ChevronDown 
              className={`w-5 h-5 transition-transform ${
                expandedMenu === 'Company' ? 'rotate-180' : ''
              }`}
            />
          </button>
          
          {expandedMenu === 'Company' && (
            <div className="ml-4 space-y-1">
              {menuItems.Company.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className="block px-4 py-2 hover:bg-purple-700 rounded-lg transition-colors text-sm"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          )}

          {/* Support Submenu */}
          <button
            onClick={() => toggleSubmenu('Support')}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-purple-700 rounded-lg transition-colors"
          >
            <span className="font-semibold">Support</span>
            <ChevronDown 
              className={`w-5 h-5 transition-transform ${
                expandedMenu === 'Support' ? 'rotate-180' : ''
              }`}
            />
          </button>
          
          {expandedMenu === 'Support' && (
            <div className="ml-4 space-y-1">
              {menuItems.Support.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className="block px-4 py-2 hover:bg-purple-700 rounded-lg transition-colors text-sm"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          )}

          {/* Legal Submenu */}
          <button
            onClick={() => toggleSubmenu('Legal')}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-purple-700 rounded-lg transition-colors"
          >
            <span className="font-semibold">Legal</span>
            <ChevronDown 
              className={`w-5 h-5 transition-transform ${
                expandedMenu === 'Legal' ? 'rotate-180' : ''
              }`}
            />
          </button>
          
          {expandedMenu === 'Legal' && (
            <div className="ml-4 space-y-1">
              {menuItems.Legal.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className="block px-4 py-2 hover:bg-purple-700 rounded-lg transition-colors text-sm"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          )}

          {/* Logout */}
          <div className="border-t border-purple-600 my-2"></div>
          <button
            onClick={() => {
              handleLogout();
              setIsMenuOpen(false);
            }}
            className="w-full text-left px-4 py-3 hover:bg-red-700 bg-red-600 rounded-lg transition-colors flex items-center space-x-3"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </div>
    )}

    {/* Navigation Tabs */}
    <nav className="flex space-x-2 mt-4 overflow-x-auto pb-2">
      {['dashboard', 'send', 'crypto', 'cards', 'wallets', 'batch', 'transactions', 'profile'].map((tab) => (
        <button
          key={tab}
          onClick={() => setActiveTab(tab)}
          className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors text-sm md:text-base ${
            activeTab === tab
              ? 'bg-blue-600 text-white'
              : 'bg-purple-800 text-purple-300 hover:bg-purple-700'
          }`}
        >
          {tab === 'batch' ? 'Batch Pay' : tab.charAt(0).toUpperCase() + tab.slice(1)}
        </button>
      ))}
    </nav>
  </div>
</header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-green-600 to-green-800 rounded-lg p-6 shadow-lg">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-green-200 text-sm">Total Balance</p>
                    <p className="text-3xl font-bold mt-2">${balances.totalUSD?.toFixed(2) || '0.00'}</p>
                  </div>
                  <DollarSign className="text-green-300" size={32} />
                </div>
                <p className="text-green-200 text-xs">Across all wallets and cards</p>
              </div>

              <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg p-6 shadow-lg">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-blue-200 text-sm">Crypto Cards</p>
                    <p className="text-3xl font-bold mt-2">{cryptoCards.length}</p>
                  </div>
                  <CreditCard className="text-blue-300" size={32} />
                </div>
                <p className="text-blue-200 text-xs">${cryptoCards.reduce((sum, card) => sum + ((parseFloat(card.balance || card.current_btc_balance || 0) * cryptoPrices.BTC) || card.denomination), 0).toFixed(2)} total value</p>
              </div>

              <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-lg p-6 shadow-lg">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-purple-200 text-sm">Active Wallets</p>
                    <p className="text-3xl font-bold mt-2">{wallets.length}</p>
                  </div>
                  <Wallet className="text-purple-300" size={32} />
                </div>
                <p className="text-purple-200 text-xs">Multi-currency support</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm">
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <Wallet className="mr-2 text-blue-400" size={24} />
                  Crypto Balances
                </h3>
                <div className="space-y-3">
                  {cryptoOptions.map((crypto) => (
                    <div key={crypto.code} className="flex justify-between items-center bg-purple-700 bg-opacity-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full bg-${crypto.color}-500 flex items-center justify-center text-xl font-bold`}>
                          {crypto.symbol}
                        </div>
                        <div>
                          <p className="font-bold">{crypto.name}</p>
                          <p className="text-sm text-purple-300">{balances[crypto.code]?.toFixed(8) || '0.00000000'} {crypto.code}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">${((balances[crypto.code] || 0) * (cryptoPrices[crypto.code] || 0)).toFixed(2)}</p>
                        <p className="text-sm text-purple-300">${cryptoPrices[crypto.code]?.toLocaleString() || '0'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm">
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <CreditCard className="mr-2 text-orange-400" size={24} />
                  Active Crypto Cards
                </h3>
                <div className="space-y-4">
                  {cryptoCards.map((card) => (
                    <div 
                      key={card.id} 
                      onClick={() => handleCardSelection(card)}
                      className={`bg-gradient-to-r from-orange-600 to-red-600 rounded-lg p-4 shadow-lg cursor-pointer transition-all ${
                        selectedCard?.id === card.id ? 'ring-4 ring-yellow-400' : 'hover:shadow-xl'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="text-sm text-orange-200">Card Number</p>
                          <p className="text-xl font-bold font-mono mt-1">{card.maskedCardNumber}</p>
                        </div>
                        <CreditCard size={32} className="text-orange-200" />
                      </div>
                      <div className="flex justify-between items-end">
                        <div>
                          <p className="text-xs text-orange-200">Balance</p>
                          <p className="text-lg font-bold">{card.balance?.toFixed(8) || "0.00000000"} BTC</p>
                          <p className="text-2xl font-bold">${((card.balance || 0) * (cryptoPrices.BTC || 0)).toFixed(2)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-orange-200">Activated</p>
                          <p className="text-sm">{new Date(card.activatedAt).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      setShowActivateCardModal(true);
                      setActivateData({ cardNumber: '', pin: '', code: '', generatedCode: '' });
                    }}
                    className="w-full bg-orange-600 hover:bg-orange-700 py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                  >
                    <Plus size={20} />
                    <span>Activate New Card</span>
                  </button>
                </div>
               </div>
            </div>

            {/* ADD THIS NEW SECTION HERE */}
            <div className="mt-6">
              <ActivatedCardsList />
            </div>
            {/* END OF NEW SECTION */}

            <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <Clock className="mr-2 text-green-400" size={24} />
                Recent Transactions
              </h3>
              <div className="space-y-3">
                {transactions.slice(0, 5).map((tx) => {
                  const direction = getTransactionDirection(tx);
                  return (
                  <div key={tx.id} className="flex items-center justify-between bg-purple-700 bg-opacity-50 rounded-lg p-4">
                    <div className="flex items-center space-x-4">
                      <div className={`p-2 rounded-full ${
                        direction === 'received' ? 'bg-green-600' :
                        direction === 'sent' ? 'bg-red-600' :
                        'bg-yellow-600'
                      }`}>
                        {direction === 'received' ? <ArrowDown size={20} /> :
                         direction === 'sent' ? <ArrowUp size={20} /> :
                         <RefreshCw size={20} />}
                      </div>
                      <div>
                        <p className="font-bold">{getTransactionTitle(tx)}</p>
                        <p className="text-sm text-purple-300">
                          {new Date(tx.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${direction === 'received' ? 'text-green-400' : 'text-red-400'}`}>
                        {direction === 'received' ? '+' : direction === 'sent' ? '-' : ''}{tx.amount} {tx.currency}
                      </p>
                      <p className={`text-xs ${
                        tx.status === 'confirmed' ? 'text-green-400' :
                        tx.status === 'pending' ? 'text-yellow-400' :
                        'text-red-400'
                      }`}>
                        {tx.status}
                      </p>
                    </div>
                  </div>
                );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <button
                onClick={() => setActiveTab('send')}
                className="bg-gradient-to-br from-green-600 to-green-800 hover:from-green-700 hover:to-green-900 rounded-lg p-6 shadow-lg transition-all transform hover:scale-105"
              >
                <Send className="text-green-200 mb-3" size={32} />
                <h4 className="text-xl font-bold mb-2">send Money</h4>
                <p className="text-green-200 text-sm">Transfer to 180+ countries</p>
              </button>

              <button
                onClick={() => setActiveTab('crypto')}
                className="bg-gradient-to-br from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 rounded-lg p-6 shadow-lg transition-all transform hover:scale-105"
              >
                <ArrowLeftRight className="text-blue-200 mb-3" size={32} />
                <h4 className="text-xl font-bold mb-2">Crypto Operations</h4>
                <p className="text-blue-200 text-sm">Send, receive & convert crypto</p>
              </button>

              <button
                onClick={() => setShowKYCUpgrade(true)}
                className="bg-gradient-to-br from-yellow-600 to-yellow-800 hover:from-yellow-700 hover:to-yellow-900 rounded-lg p-6 shadow-lg transition-all transform hover:scale-105"
              >
                <Shield className="text-yellow-200 mb-3" size={32} />
                <h4 className="text-xl font-bold mb-2">Upgrade KYC</h4>
                <p className="text-yellow-200 text-sm">Increase your limits</p>
              </button>
            </div>
          </div>
        )}
       
       {activeTab === 'send' && (
  <SendMoneyPage user={user} />
)}
         {activeTab === 'crypto' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <button
                onClick={() => setShowSendCryptoModal(true)}
                className="bg-gradient-to-br from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 rounded-lg p-6 shadow-lg transition-all transform hover:scale-105"
              >
                <Send className="text-blue-200 mb-3" size={32} />
                <h4 className="text-xl font-bold mb-2">Send Crypto</h4>
                <p className="text-blue-200 text-sm">Transfer to any wallet</p>
              </button>

              <button
                onClick={() => { 
                  setSelectedWallet(wallets[0] || null);
                  setShowReceiveCryptoModal(true); 
                }}
                className="bg-gradient-to-br from-green-600 to-green-800 hover:from-green-700 hover:to-green-900 rounded-lg p-6 shadow-lg transition-all transform hover:scale-105"
              >
                <ArrowDown className="text-green-200 mb-3" size={32} />
                <h4 className="text-xl font-bold mb-2">Receive Crypto</h4>
                <p className="text-green-200 text-sm">Get your wallet address</p>
              </button>

              <button
                onClick={() => setShowConvertCryptoModal(true)}
                className="bg-gradient-to-br from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 rounded-lg p-6 shadow-lg transition-all transform hover:scale-105"
              >
                <ArrowLeftRight className="text-purple-200 mb-3" size={32} />
                <h4 className="text-xl font-bold mb-2">Convert Crypto</h4>
                <p className="text-purple-200 text-sm">Swap between coins</p>
              </button>

              <button
                onClick={() => setShowCryptoToCashModal(true)}
                className="bg-gradient-to-br from-yellow-600 to-yellow-800 hover:from-yellow-700 hover:to-yellow-900 rounded-lg p-6 shadow-lg transition-all transform hover:scale-105"
              >
                <DollarSign className="text-yellow-200 mb-3" size={32} />
                <h4 className="text-xl font-bold mb-2">Crypto to Cash</h4>
                <p className="text-yellow-200 text-sm">Withdraw to bank</p>
              </button>
            </div>

            <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <TrendingUp className="mr-2 text-green-400" size={24} />
                Live Crypto Prices
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {cryptoOptions.map((crypto) => (
                  <div key={crypto.code} className="bg-purple-700 bg-opacity-50 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-bold text-lg">{crypto.name}</p>
                        <p className="text-sm text-purple-300">{crypto.code}</p>
                      </div>
                      <div className={`text-2xl font-bold text-${crypto.color}-400`}>
                        {crypto.symbol}
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-green-400">
                      ${cryptoPrices[crypto.code]?.toLocaleString() || '0'}
                    </p>
                    <p className="text-sm text-purple-300">
                      Balance: {balances[crypto.code]?.toFixed(8) || '0.00000000'}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm">
              <h3 className="text-xl font-bold mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => setActiveTab('cards')}
                  className="bg-purple-700 hover:bg-purple-600 p-4 rounded-lg text-left transition-colors"
                >
                  <CreditCard className="text-orange-400 mb-2" size={24} />
                  <p className="font-bold">Buy Crypto with Card</p>
                  <p className="text-sm text-purple-300">Use debit/credit card</p>
                </button>

                <button
                  onClick={() => setShowKYCUpgrade(true)}
                  className="bg-purple-700 hover:bg-purple-600 p-4 rounded-lg text-left transition-colors"
                >
                  <Shield className="text-yellow-400 mb-2" size={24} />
                  <p className="font-bold">Increase Limits</p>
                  <p className="text-sm text-purple-300">Complete KYC verification</p>
                </button>

                {/* ✅ Batch Payments quick-action card */}
                <button
                  onClick={() => setActiveTab('batch')}
                  className="bg-gradient-to-br from-green-800 to-green-900 hover:from-green-700 hover:to-green-800 p-4 rounded-lg text-left transition-all border border-green-600 col-span-1 md:col-span-2"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="text-green-400" size={22} />
                    <p className="font-bold text-white">Batch Payments</p>
                    <span className="ml-auto text-xs bg-green-700 text-green-200 px-2 py-0.5 rounded-full font-semibold">New</span>
                  </div>
                  <p className="text-sm text-green-300">Pay employees, contractors &amp; vendors — CSV upload, auto-scheduling, save up to 95% on fees</p>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ✅ BATCH PAYMENTS TAB */}
        {activeTab === 'batch' && (
          <BatchPaymentsPage user={user} />
        )}

        {activeTab === 'cards' && (
          <div className="space-y-6">
            <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm">
              <h2 className="text-2xl font-bold mb-6 flex items-center">
                <CreditCard className="mr-3 text-orange-400" size={32} />
                My Crypto Cards
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {cryptoCards.map((card) => (
                  <div 
                    key={card.id} 
                    onClick={() => handleCardSelection(card)}
                    className={`bg-gradient-to-br from-orange-600 to-red-600 rounded-xl p-6 shadow-xl cursor-pointer transition-all ${
                      selectedCard?.id === card.id ? 'ring-4 ring-yellow-400 scale-105' : 'hover:shadow-2xl'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <img 
                          src="/zanors-logo-full.png" 
                          alt="Zanors" 
                          className="h-12 mb-1 opacity-90" 
                          style={{ mixBlendMode: 'screen' }}
                        />
                        <p className="text-xs text-orange-300">Crypto Prepaid Card</p>
                      </div>
                      <CreditCard size={32} className="text-orange-200" />
                    </div>
                    <p className="text-2xl font-bold font-mono mb-4">{card.maskedCardNumber}</p>
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-xs text-orange-200">Available Balance</p>
                        <p className="text-lg font-bold">{card.balance?.toFixed(8) || "0.00000000"} BTC</p>
                        <p className="text-3xl font-bold">${((card.balance || 0) * (cryptoPrices.BTC || 0)).toFixed(2)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-orange-200">Status</p>
                        <p className="text-sm font-bold text-green-300">Active</p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-orange-500 flex justify-between text-xs">
                      <div>
                        <p className="text-orange-300">Activated</p>
                        <p className="font-bold">{new Date(card.activatedAt).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-orange-300">Token ID</p>
                        <p className="font-bold">{card.tokenId}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
             <button
                  onClick={() => {
                    setShowActivateCardModal(true);
                    setActivateData({ cardNumber: '', pin: '', code: '', generatedCode: '' });
                  }}
                  className="bg-green-600 hover:bg-green-700 p-4 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                >
                  <Plus size={20} />
                  <span>Activate New Card</span>
                </button>

                <button
                  onClick={() => setShowTopUpCardModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 p-4 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                >
                  <Wallet size={20} />
                  <span>Top Up Card</span>
                </button>

                <button
                  onClick={handleViewCardHistory}
                  className="bg-purple-600 hover:bg-purple-700 p-4 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                >
                  <History size={20} />
                  <span>Card History</span>
                </button>
              </div>
            </div>

            <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm">
              <h3 className="text-xl font-bold mb-4">How Crypto Cards Work</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="bg-green-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl font-bold">1</span>
                  </div>
                  <h4 className="font-bold mb-2">Purchase Card</h4>
                  <p className="text-sm text-purple-300">Buy crypto gift cards at major retailers nationwide</p>
                </div>
                <div className="text-center">
                  <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl font-bold">2</span>
                  </div>
                  <h4 className="font-bold mb-2">Activate Online</h4>
                  <p className="text-sm text-purple-300">Enter card number and PIN to activate your crypto</p>
                </div>
                <div className="text-center">
                  <div className="bg-purple-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl font-bold">3</span>
                  </div>
                  <h4 className="font-bold mb-2">Use Instantly</h4>
                  <p className="text-sm text-purple-300">Send, trade, or convert your crypto immediately</p>
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'wallets' && (
          <div className="space-y-6">
            <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm">
              <h2 className="text-2xl font-bold mb-6 flex items-center">
                <Wallet className="mr-3 text-blue-400" size={32} />
                My Crypto Wallets
              </h2>

              <div className="space-y-4">
                {wallets.map((wallet) => (
                  <div key={wallet.id} className="bg-purple-700 bg-opacity-50 rounded-lg p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="text-lg font-bold mb-1">{wallet.name}</p>
                        <p className="text-sm text-purple-300">Wallet Address</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <p className="font-mono text-lg">{wallet.shortAddress}</p>
                          <button
                            onClick={() => copyToClipboard(wallet.address)}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            {copied === wallet.address ? <Check size={18} /> : <Copy size={18} />}
                          </button>
                        </div>
                        <p className="text-xs text-purple-400 mt-1">{wallet.address}</p>
                      </div>
                      <div className="flex space-x-2 flex-wrap">
                        {wallet.isCard ? (
                          // Buttons for activated card wallets - ONLY Send and Withdraw
                          <>
                            <button
                              onClick={() => {
                                setSelectedCardWallet(wallet);
                                setShowSendFromCardModal(true);
                              }}
                              className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm transition-colors flex items-center space-x-1"
                            >
                              <Send size={16} />
                              <span>Send</span>
                            </button>
                            <button
                              onClick={() => {
                                setSelectedCardWallet(wallet);
                                setShowWithdrawFromCardModal(true);
                              }}
                              className="bg-yellow-600 hover:bg-yellow-700 px-4 py-2 rounded-lg text-sm transition-colors flex items-center space-x-1"
                            >
                              <ArrowDown size={16} />
                              <span>Withdraw</span>
                            </button>
                          </>
                        ) : (
                          // Buttons for manually created wallets - Receive, Send, Withdraw, Top Up
                          <>
                            <button
                              onClick={() => {
                                setSelectedWallet(wallet);
                                setShowReceiveCryptoModal(true);
                              }}
                              className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg text-sm transition-colors flex items-center space-x-1"
                            >
                              <ArrowDown size={16} />
                              <span>Receive</span>
                            </button>
                            <button
                              onClick={() => {
                                setSelectedCardWallet(wallet);
                                setShowSendFromCardModal(true);
                              }}
                              className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm transition-colors flex items-center space-x-1"
                            >
                              <Send size={16} />
                              <span>Send</span>
                            </button>
                            <button
                              onClick={() => {
                                setSelectedCardWallet(wallet);
                                setShowWithdrawFromCardModal(true);
                              }}
                              className="bg-yellow-600 hover:bg-yellow-700 px-4 py-2 rounded-lg text-sm transition-colors flex items-center space-x-1"
                            >
                              <ArrowDown size={16} />
                              <span>Withdraw</span>
                            </button>
                            <button
                              onClick={() => {
                                setSelectedCard(wallet.cardData);
                                setShowTopUpCardModal(true);
                              }}
                              className="bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg text-sm transition-colors flex items-center space-x-1"
                            >
                              <Plus size={16} />
                              <span>Top Up</span>
                            </button>
                            <button
                              onClick={() => handleRevealKeyClick(wallet)}
                              className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg text-sm transition-colors flex items-center space-x-1"
                            >
                              <Lock size={16} />
                              <span>Keys</span>
                            </button>
                          </>
                        )}
                      </div>

                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                      {Object.entries(wallet.balances).map(([crypto, balance]) => (
                        <div key={crypto} className="bg-purple-800 rounded-lg p-3">
                          <p className="text-xs text-purple-300">{crypto}</p>
                          <p className="font-bold text-sm">{balance}</p>
                          <p className="text-xs text-purple-400">
                            ${((parseFloat(balance) || 0) * (cryptoPrices[crypto] || 0)).toFixed(2)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => setShowCreateWalletModal(true)}
                className="w-full bg-blue-600 hover:bg-blue-700 py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 mt-4"
              >
                <Plus size={20} />
                <span>Create New Wallet</span>
              </button>
            </div>

            <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm">
              <h3 className="text-xl font-bold mb-4">Wallet Security</h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-bold">Multi-Signature Protection</p>
                    <p className="text-sm text-purple-300">Multiple approvals required for large transactions</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-bold">Cold Storage</p>
                    <p className="text-sm text-purple-300">95% of funds stored offline in secure vaults</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-green-400 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="font-bold">Insurance Coverage</p>
                    <p className="text-sm text-purple-300">$200M cryptocurrency custody insurance</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'transactions' && (
          <div className="space-y-6">
            <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm">
              <h2 className="text-2xl font-bold mb-6 flex items-center">
                <Clock className="mr-3 text-green-400" size={32} />
                Transaction History
              </h2>

              <div className="mb-4 flex flex-wrap gap-2">
                <button 
                  onClick={() => setTransactionFilter('all')}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                    transactionFilter === 'all' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-purple-700 hover:bg-purple-600 text-purple-300'
                  }`}
                >
                  All
                </button>
                <button 
                  onClick={() => setTransactionFilter('sent')}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                    transactionFilter === 'sent' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-purple-700 hover:bg-purple-600 text-purple-300'
                  }`}
                >
                  Sent
                </button>
                <button 
                  onClick={() => setTransactionFilter('received')}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                    transactionFilter === 'received' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-purple-700 hover:bg-purple-600 text-purple-300'
                  }`}
                >
                  Received
                </button>
                <button 
                  onClick={() => setTransactionFilter('convert')}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                    transactionFilter === 'convert' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-purple-700 hover:bg-purple-600 text-purple-300'
                  }`}
                >
                  Converted
                </button>
                <button 
                  onClick={() => setTransactionFilter('withdraw')}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                    transactionFilter === 'withdraw' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-purple-700 hover:bg-purple-600 text-purple-300'
                  }`}
                >
                  Withdrawn
                </button>
              </div>

              <div className="space-y-3">
                {getFilteredTransactions().map((tx) => {
                  const direction = getTransactionDirection(tx);
                  return (
                  <div key={tx.id} className="bg-purple-700 bg-opacity-50 rounded-lg p-4 hover:bg-opacity-70 transition-all">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className={`p-3 rounded-full ${
                          direction === 'received' ? 'bg-green-600' :
                          direction === 'sent' ? 'bg-red-600' :
                          'bg-yellow-600'
                        }`}>
                          {direction === 'received' ? <ArrowDown size={20} /> :
                           direction === 'sent' ? <ArrowUp size={20} /> :
                           <RefreshCw size={20} />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <p className="font-bold">{getTransactionTitle(tx)}</p>
                            <span className={`text-xs px-2 py-1 rounded ${
                              tx.status === 'confirmed' ? 'bg-green-600' :
                              tx.status === 'pending' ? 'bg-yellow-600' :
                              'bg-red-600'
                            }`}>
                              {tx.status}
                            </span>
                          </div>
                          <p className="text-sm text-purple-300">
                            {new Date(tx.timestamp).toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                          {tx.from && <p className="text-xs text-purple-400">From: {tx.from}</p>}
                          {tx.to && <p className="text-xs text-purple-400">To: {tx.to}</p>}
                          {tx.txHash && (
                            <div className="flex items-center space-x-2 mt-1">
                              <p className="text-xs text-purple-400 font-mono">{tx.txHash}</p>
                              <button
                                onClick={() => copyToClipboard(tx.txHash)}
                                className="text-blue-400 hover:text-blue-300"
                              >
                                {copied === tx.txHash ? <Check size={14} /> : <Copy size={14} />}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold text-lg ${direction === 'received' ? 'text-green-400' : direction === 'sent' ? 'text-red-400' : 'text-yellow-400'}`}>
                          {direction === 'received' ? '+' : direction === 'sent' ? '-' : ''}{tx.amount} {tx.currency}
                        </p>
                        {tx.type === 'convert' && (
                          <p className="text-sm text-purple-300">
                            {tx.from} → {tx.to}
                          </p>
                        )}
                        <p className="text-xs text-purple-400">
                          ${((parseFloat(tx.amount) || 0) * (cryptoPrices[tx.currency] || exchangeRates[tx.currency] || 1)).toFixed(2)} USD
                        </p>
                      </div>
                    </div>
                  </div>
                );
                })}
              </div>

              {getFilteredTransactions().length === 0 && (
                <div className="text-center py-12">
                  <Clock className="text-purple-500 mx-auto mb-4" size={48} />
                  <p className="text-purple-300">No transactions yet</p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6">
                <p className="text-sm text-purple-300 mb-2">Total Sent</p>
                <p className="text-2xl font-bold">
                  ${transactions
                    .filter(tx => tx.type === 'sent')
                    .reduce((sum, tx) => sum + (parseFloat(tx.amount) * (cryptoPrices[tx.currency] || 1)), 0)
                    .toFixed(2)}
                </p>
              </div>
              <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6">
                <p className="text-sm text-purple-300 mb-2">Total Received</p>
                <p className="text-2xl font-bold text-green-400">
                  ${transactions
                    .filter(tx => tx.type === 'received')
                    .reduce((sum, tx) => sum + (parseFloat(tx.amount) * (cryptoPrices[tx.currency] || 1)), 0)
                    .toFixed(2)}
                </p>
              </div>
              <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6">
                <p className="text-sm text-purple-300 mb-2">Total Transactions</p>
                <p className="text-2xl font-bold">{transactions.length}</p>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm">
              <h2 className="text-2xl font-bold mb-6 flex items-center">
                <User className="mr-3 text-purple-400" size={32} />
                Profile Settings
              </h2>

              <div className="space-y-6">
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    {profileData.profilePicture ? (
                      <img src={profileData.profilePicture} alt="Profile" className="w-24 h-24 rounded-full object-cover" />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-purple-600 flex items-center justify-center">
                        <User size={48} />
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePictureUpload}
                      className="hidden"
                      id="profilePicture"
                    />
                    <label
                      htmlFor="profilePicture"
                      className="absolute bottom-0 right-0 bg-blue-600 hover:bg-blue-700 p-2 rounded-full cursor-pointer"
                    >
                      <Camera size={16} />
                    </label>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{user?.firstName} {user?.lastName}</p>
                    <p className="text-purple-300">{user?.email}</p>
                    <p className="text-purple-300">{user?.phone}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">First Name</label>
                    <input
                      type="text"
                      value={profileData.firstName}
                      onChange={(e) => setProfileData({...profileData, firstName: e.target.value})}
                      className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Last Name</label>
                    <input
                      type="text"
                      value={profileData.lastName}
                      onChange={(e) => setProfileData({...profileData, lastName: e.target.value})}
                      className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                    className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Phone Number</label>
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                    className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
                  />
                </div>

                <div className="bg-purple-700 rounded-lg p-4">
                  <h4 className="font-bold mb-3">Notification Preferences</h4>
                  <div className="space-y-3">
                    {Object.entries(profileData.notifications).map(([key, value]) => (
                      <label key={key} className="flex items-center justify-between cursor-pointer">
                        <span className="capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                        <button
                          onClick={() => setProfileData({
                            ...profileData,
                            notifications: {...profileData.notifications, [key]: !value}
                          })}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            value ? 'bg-green-600' : 'bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              value ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Security Settings */}
                <div className="bg-purple-700 rounded-lg p-4 border-t-2 border-blue-500">
                  <h4 className="font-bold mb-3 flex items-center">
                    <Shield className="mr-2 text-green-400" size={20} />
                    Security Settings
                  </h4>
                  <button
                    onClick={() => setShowPinSetup(true)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded flex items-center justify-center space-x-2 transition-colors"
                  >
                    <Lock size={20} />
                    <span>Set Transaction PIN</span>
                  </button>
                </div>

                <div className="bg-yellow-900 bg-opacity-40 rounded-lg p-4 border border-yellow-600">
                  <div className="flex items-start space-x-3">
                    <Shield className="text-yellow-400 flex-shrink-0 mt-1" size={20} />
                    <div className="flex-1">
                      <p className="font-bold text-yellow-300">KYC Status: {user?.kycLevel === 'minimal' ? 'Basic' : 'Verified'}</p>
                      <p className="text-sm text-yellow-200 mt-1">
                        {user?.kycLevel === 'minimal' 
                          ? `Daily limit: $${spendingLimit.daily} | Complete KYC to increase limits`
                          : `Monthly limit: $${spendingLimit.monthly} | Full access enabled`
                        }
                      </p>
                      {user?.kycLevel === 'minimal' && (
                        <button
                          onClick={() => setShowKYCUpgrade(true)}
                          className="mt-2 bg-yellow-600 hover:bg-yellow-700 px-4 py-2 rounded text-sm transition-colors"
                        >
                          Upgrade KYC
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => alert('Profile updated successfully!')}
                  className="w-full bg-blue-600 hover:bg-blue-700 py-3 rounded-lg font-medium transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {showSendCryptoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-purple-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Send Cryptocurrency</h3>
              <button onClick={() => setShowSendCryptoModal(false)} className="text-purple-300 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Send From</label>
                <select
                  value={sendCryptoData.sendFrom}
                  onChange={(e) => setSendCryptoData({...sendCryptoData, sendFrom: e.target.value})}
                  className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
                >
                  <option value="">Select Wallet or Card</option>
                  
                  {/* Manual Wallets */}
                  {wallets.filter(w => !w.isCard).map(wallet => (
                    <option key={`wallet-${wallet.id}`} value={`wallet-${wallet.id}`}>
                      {wallet.name} - {wallet.balances?.BTC || '0.00000000'} BTC
                    </option>
                  ))}
                  
                  {/* Activated Card Wallets */}
                  {wallets.filter(w => w.isCard).map(wallet => (
                    <option key={`card-${wallet.id}`} value={`card-${wallet.id}`}>
                      {wallet.name} - {wallet.balances?.BTC || '0.00000000'} BTC
                    </option>
                  ))}
                  
                  {/* Show message if no wallets or cards */}
                  {wallets.length === 0 && (
                    <option value="" disabled>Create a wallet or activate a card first</option>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Select Crypto</label>
                <select
                  value={sendCryptoData.crypto}
                  onChange={(e) => setSendCryptoData({...sendCryptoData, crypto: e.target.value})}
                  className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
                >
                  {cryptoOptions.map(crypto => (
                    <option key={crypto.code} value={crypto.code}>
                      {crypto.symbol} {crypto.name} - {balances[crypto.code]?.toFixed(8) || '0.00000000'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Recipient Wallet Address</label>
                <input
                  type="text"
                  value={sendCryptoData.recipientAddress}
                  onChange={(e) => setSendCryptoData({...sendCryptoData, recipientAddress: e.target.value})}
                  className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
                  placeholder="0x... or bc1q..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Amount</label>
                <input
                  type="number"
                  step="0.00000001"
                  value={sendCryptoData.amount}
                  onChange={(e) => setSendCryptoData({...sendCryptoData, amount: e.target.value})}
                  className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
                  placeholder="0.00000000"
                />
                <p className="text-sm text-purple-300 mt-1">
                  Available: {(() => {
                    if (!sendCryptoData.sendFrom) return '0.00000000';
                    
                    const selectedWallet = wallets.find(w => {
                      const walletKey = w.isCard ? `card-${w.id}` : `wallet-${w.id}`;
                      return walletKey === sendCryptoData.sendFrom;
                    });
                    
                    if (!selectedWallet) return '0.00000000';
                    
                    const balance = parseFloat(selectedWallet.balances?.BTC || selectedWallet.btc_balance || 0);
                    return balance.toFixed(8);
                  })()} {sendCryptoData.crypto}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Note (Optional)</label>
                <input
                  type="text"
                  value={sendCryptoData.note}
                  onChange={(e) => setSendCryptoData({...sendCryptoData, note: e.target.value})}
                  className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
                  placeholder="Payment for..."
                />
              </div>

              <div className="bg-blue-900 bg-opacity-40 rounded-lg p-3 border border-blue-600">
                <div className="flex justify-between text-sm">
                  <span>Value in USD:</span>
                  <span className="font-bold">
                    ${((parseFloat(sendCryptoData.amount) || 0) * (cryptoPrices[sendCryptoData.crypto] || 0)).toFixed(2)}
                  </span>
                </div>
              </div>

              <button
                onClick={handleSendCrypto}
                disabled={loading || !sendCryptoData.sendFrom || !sendCryptoData.recipientAddress || !sendCryptoData.amount}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed py-3 rounded-lg font-medium transition-colors"
              >
                {loading ? 'Processing...' : 'Send Crypto'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReceiveCryptoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-purple-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Receive Cryptocurrency</h3>
              <button onClick={() => {
                setShowReceiveCryptoModal(false);
                setSelectedWallet(null);
              }} className="text-purple-300 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Select Crypto to Receive</label>
                <select
                  value={selectedCrypto}
                  onChange={(e) => setSelectedCrypto(e.target.value)}
                  className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
                >
                  {cryptoOptions.map(crypto => (
                    <option key={crypto.code} value={crypto.code}>
                      {crypto.symbol} {crypto.name}
                    </option>
                  ))}
                </select>
              </div>
              {console.log("Debug modal:", { wallets: wallets.length, selectedCrypto, walletAddresses, firstWallet: wallets[0]?.wallet_addresses })}
                 {selectedWallet?.wallet_addresses && (
                  <div className="bg-purple-700 rounded-lg p-4 text-center">
                    {/* QR Code */}
                    <div className="bg-white p-4 rounded-lg mb-3 inline-block">
                      {(selectedWallet?.wallet_addresses?.[selectedCrypto]) ? (
                        <QRCodeSVG
                          value={selectedWallet?.wallet_addresses?.[selectedCrypto]}
                          includeMargin={true}
                        />
                      ) : (
                        <div className="w-32 h-32 flex items-center justify-center text-gray-400">
                          No Address
                        </div>
                      )}
                    </div>

                    {/* Wallet Address */}
                    <div className="bg-purple-900 rounded p-3 break-all font-mono text-sm">
                      {selectedWallet?.wallet_addresses?.[selectedCrypto] || "Address not available"}
                    </div>

                    {/* Copy Button */}
                    <button
                      onClick={() => copyToClipboard(selectedWallet?.wallet_addresses?.[selectedCrypto] || 'Address not available')}
                      className="mt-3 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg flex items-center space-x-2 mx-auto transition-colors"
                    >
                      {copied === selectedWallet?.wallet_addresses?.[selectedCrypto] ? (
                        <>
                          <Check size={18} />
                          <span>Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy size={18} />
                         <span>Copy Address</span>
                        </>
                      )}
                    </button>
                  </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showConvertCryptoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-purple-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Convert Cryptocurrency</h3>
              <button onClick={() => setShowConvertCryptoModal(false)} className="text-purple-300 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">From</label>
                <select
                  value={convertCryptoData.fromCrypto}
                  onChange={(e) => setConvertCryptoData({...convertCryptoData, fromCrypto: e.target.value})}
                  className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
                >
                  {cryptoOptions.map(crypto => (
                    <option key={crypto.code} value={crypto.code}>
                      {crypto.symbol} {crypto.name} - {balances[crypto.code]?.toFixed(8) || '0.00000000'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="text-center">
                <button
                  onClick={() => setConvertCryptoData({
                    fromCrypto: convertCryptoData.toCrypto,
                    toCrypto: convertCryptoData.fromCrypto,
                    amount: convertCryptoData.amount
                  })}
                  className="bg-purple-700 hover:bg-purple-600 p-2 rounded-full transition-colors"
                >
                  <Repeat size={24} />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">To</label>
                <select
                  value={convertCryptoData.toCrypto}
                  onChange={(e) => setConvertCryptoData({...convertCryptoData, toCrypto: e.target.value})}
                  className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
                >
                  {cryptoOptions.filter(c => c.code !== convertCryptoData.fromCrypto).map(crypto => (
                    <option key={crypto.code} value={crypto.code}>
                      {crypto.symbol} {crypto.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Amount to Convert</label>
                <input
                  type="number"
                  step="0.00000001"
                  value={convertCryptoData.amount}
                  onChange={(e) => setConvertCryptoData({...convertCryptoData, amount: e.target.value})}
                  className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
                  placeholder="0.00000000"
                />
                <p className="text-sm text-purple-300 mt-1">
                  Available: {balances[convertCryptoData.fromCrypto]?.toFixed(8) || '0.00000000'} {convertCryptoData.fromCrypto}
                </p>
              </div>

              <div className="bg-blue-900 bg-opacity-40 rounded-lg p-4 border border-blue-600">
                <div className="flex justify-between mb-2">
                  <span>You Send:</span>
                  <span className="font-bold">{convertCryptoData.amount || '0'} {convertCryptoData.fromCrypto}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span>You Receive (est):</span>
                  <span className="font-bold text-green-400">
                    {calculateCryptoConversion(convertCryptoData.amount, convertCryptoData.fromCrypto, convertCryptoData.toCrypto)} {convertCryptoData.toCrypto}
                  </span>
                </div>
                <div className="flex justify-between border-t border-blue-700 pt-2 text-sm">
                  <span>Rate:</span>
                  <span>1 {convertCryptoData.fromCrypto} = {(cryptoPrices[convertCryptoData.fromCrypto] / cryptoPrices[convertCryptoData.toCrypto]).toFixed(8)} {convertCryptoData.toCrypto}</span>
                </div>
              </div>

              <button
                onClick={handleConvertCrypto}
                disabled={loading || !convertCryptoData.amount}
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed py-3 rounded-lg font-medium transition-colors"
              >
                {loading ? 'Processing...' : 'Convert Now'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showCryptoToCashModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-purple-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Convert Crypto to Cash</h3>
              <button onClick={() => setShowCryptoToCashModal(false)} className="text-purple-300 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Select Crypto</label>
                <select
                  value={cryptoToCashData.crypto}
                  onChange={(e) => setCryptoToCashData({...cryptoToCashData, crypto: e.target.value})}
                  className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
                >
                  {cryptoOptions.map(crypto => (
                    <option key={crypto.code} value={crypto.code}>
                      {crypto.symbol} {crypto.name} - {balances[crypto.code]?.toFixed(8) || '0.00000000'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Amount</label>
                <input
                  type="number"
                  step="0.00000001"
                  value={cryptoToCashData.amount}
                  onChange={(e) => setCryptoToCashData({...cryptoToCashData, amount: e.target.value})}
                  className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
                  placeholder="0.00000000"
                />
                <p className="text-sm text-purple-300 mt-1">
                  Available: {balances[cryptoToCashData.crypto]?.toFixed(8) || '0.00000000'} {cryptoToCashData.crypto}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Convert To</label>
                <select
                  value={cryptoToCashData.currency}
                  onChange={(e) => setCryptoToCashData({...cryptoToCashData, currency: e.target.value})}
                  className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
                >
                  {currencies.map(curr => (
                    <option key={curr.code} value={curr.code}>
                      {curr.flag} {curr.code} - {curr.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Withdrawal Method</label>
                <select
                  value={cryptoToCashData.withdrawMethod}
                  onChange={(e) => setCryptoToCashData({...cryptoToCashData, withdrawMethod: e.target.value})}
                  className="w-full bg-purple-700 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
                >
                  <option value="bank">Bank Transfer</option>
                  <option value="card">Debit Card</option>
                  <option value="mobile">Mobile Money</option>
                </select>
              </div>

              <div className="bg-blue-900 bg-opacity-40 rounded-lg p-4 border border-blue-600">
                <div className="flex justify-between mb-2">
                  <span>You Send:</span>
                  <span className="font-bold">{cryptoToCashData.amount || '0'} {cryptoToCashData.crypto}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span>You Receive (est):</span>
                  <span className="font-bold text-green-400">
                    {formatCurrency(
                      calculateCryptoToCash(cryptoToCashData.amount, cryptoToCashData.crypto, cryptoToCashData.currency),
                      cryptoToCashData.currency
                    )}
                  </span>
                </div>
                <div className="flex justify-between border-t border-blue-700 pt-2 text-sm">
                  <span>Rate:</span>
                  <span>1 {cryptoToCashData.crypto} = {formatCurrency(cryptoPrices[cryptoToCashData.crypto] * (exchangeRates[cryptoToCashData.currency] || 1), cryptoToCashData.currency)}</span>
                </div>
              </div>

              <button
                onClick={handleCryptoToCash}
                disabled={loading || !cryptoToCashData.amount}
                className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed py-3 rounded-lg font-medium transition-colors"
              >
                {loading ? 'Processing...' : 'Withdraw to Bank'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* PIN Verification Modal - New Secure Version */}
      <PinVerificationModal
        showPinModal={showPinModal}
        setShowPinModal={setShowPinModal}
        onSuccess={handlePinSuccess}
        onCancel={handlePinCancel}
        transactionType={pendingTransaction?.type}
        user={user}
      />

      {/* PIN Setup Modal */}
      {showPinSetup && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
          <div className="bg-purple-900 rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold flex items-center space-x-2">
                <Lock className="text-yellow-400" size={24} />
                <span>Set Transaction PIN</span>
              </h3>
              <button onClick={() => {
                setShowPinSetup(false);
                setPinError('');
                setPinSetupData({ pin: '', confirmPin: '', password: '' });
              }}>
                <X size={24} />
              </button>
            </div>

            {pinError && (
              <div className="mb-4 p-3 bg-red-900 bg-opacity-50 border border-red-600 rounded text-red-200 text-sm">
                {pinError}
              </div>
            )}

            <div className="space-y-4">
              <p className="text-sm text-purple-300">
                Set a 4-digit PIN to authorize all transactions (send, withdraw, top-up, convert).
              </p>

              <div>
                <label className="block text-sm font-medium mb-2">Create 4-Digit PIN</label>
                <input
                  type="password"
                  value={pinSetupData.pin}
                  onChange={(e) => setPinSetupData({...pinSetupData, pin: e.target.value.replace(/\D/g, '').slice(0, 4)})}
                  className="w-full bg-purple-800 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none text-center text-2xl tracking-widest"
                  placeholder="••••"
                  maxLength={4}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Confirm PIN</label>
                <input
                  type="password"
                  value={pinSetupData.confirmPin}
                  onChange={(e) => setPinSetupData({...pinSetupData, confirmPin: e.target.value.replace(/\D/g, '').slice(0, 4)})}
                  className="w-full bg-purple-800 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none text-center text-2xl tracking-widest"
                  placeholder="••••"
                  maxLength={4}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Account Password (for verification)</label>
                <input
                  type="password"
                  value={pinSetupData.password}
                  onChange={(e) => setPinSetupData({...pinSetupData, password: e.target.value})}
                  className="w-full bg-purple-800 text-white p-3 rounded border border-purple-600 focus:border-purple-400 focus:outline-none"
                  placeholder="Enter your account password"
                />
              </div>

              <button
                onClick={handleSetTransactionPin}
                disabled={loading || pinSetupData.pin.length !== 4 || pinSetupData.confirmPin.length !== 4}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Setting PIN...' : 'Set Transaction PIN'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showKYCUpgrade && <KYCUpgradeModal />}

      {/* 3D Face Capture Modal */}
      {show3DFaceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
          <div className="bg-purple-800 rounded-lg p-6 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">3D Face Verification</h3>
              <button 
                onClick={() => {
                  stopCamera();
                  setShow3DFaceModal(false);
                }} 
                className="text-purple-300 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>

            {captureStep !== 'complete' ? (
              <div className="space-y-4">
                <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
                  {isCameraActive ? (
                    <>
                      <video
                        id="faceVideo"
                        autoPlay
                        playsInline
                        muted
                        ref={(video) => {
                          if (video && videoStream) {
                            video.srcObject = videoStream;
                          }
                        }}
                        className="w-full h-full object-cover"
                      />
                      
                      {/* Oval Face Guide Overlay */}
                      <svg
                        className="absolute inset-0 w-full h-full pointer-events-none"
                        style={{ zIndex: 10 }}
                      >
                        {/* Dark overlay with oval cutout */}
                        <defs>
                          <mask id="ovalMask">
                            <rect width="100%" height="100%" fill="white" />
                            <ellipse
                              cx="50%"
                              cy="50%"
                              rx={`${ovalDimensions.rx}%`}
                              ry={`${ovalDimensions.ry}%`}
                              fill="black"
                            />
                          </mask>
                        </defs>
                        <rect
                          width="100%"
                          height="100%"
                          fill="rgba(0, 0, 0, 0.6)"
                          mask="url(#ovalMask)"
                        />
                        {/* Oval border guide */}
                        <ellipse
                          cx="50%"
                          cy="50%"
                          rx={`${ovalDimensions.rx}%`}
                          ry={`${ovalDimensions.ry}%`}
                          fill="none"
                          stroke={detectedFaceBox ? "#10b981" : "#ef4444"}
                          strokeWidth="3"
                          strokeDasharray="10,5"
                          className="animate-pulse"
                        />
                      </svg>
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-400">Initializing camera...</p>
                    </div>
                  )}
                  
                  <div className="absolute top-4 left-0 right-0 text-center" style={{ zIndex: 20 }}>
                    <div className="bg-black bg-opacity-70 inline-block px-4 py-2 rounded-lg">
                      <p className="text-white font-bold text-lg">
                        {captureStep === 'front' && 'Position your face in the oval'}
                        {captureStep === 'left' && 'Turn LEFT - Position in oval'}
                        {captureStep === 'right' && 'Turn RIGHT - Position in oval'}
                      </p>
                      <p className="text-green-400 text-sm mt-1">Click capture when well positioned</p>
                    </div>
                  </div>

                  <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-3" style={{ zIndex: 20 }}>
                    <div className={`w-3 h-3 rounded-full ${faceCaptures.front ? 'bg-green-500' : 'bg-gray-500'}`} />
                    <div className={`w-3 h-3 rounded-full ${faceCaptures.left ? 'bg-green-500' : 'bg-gray-500'}`} />
                    <div className={`w-3 h-3 rounded-full ${faceCaptures.right ? 'bg-green-500' : 'bg-gray-500'}`} />
                  </div>
                </div>

                <div className="bg-purple-700 rounded-lg p-4">
                  <h4 className="font-bold mb-2">Instructions:</h4>
                  <ul className="text-sm space-y-1">
                    <li>✓ Position your face inside the green oval</li>
                    <li>✓ Hold your ID card visible next to your face</li>
                    <li>✓ Turn your head left when prompted</li>
                    <li>✓ Turn your head right when prompted</li>
                    <li>✓ Click "Capture" button manually</li>
                  </ul>
                </div>

                {isCameraActive && (
                  <button
                    onClick={handleAutoCapture}
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-bold"
                  >
                    📸 Manual Capture - {captureStep.charAt(0).toUpperCase() + captureStep.slice(1)} View
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="text-center text-sm mb-2">Front</p>
                    <img src={faceCaptures.front} alt="Front view" className="w-full rounded-lg border-2 border-green-500" />
                  </div>
                  <div>
                    <p className="text-center text-sm mb-2">Left</p>
                    <img src={faceCaptures.left} alt="Left view" className="w-full rounded-lg border-2 border-green-500" />
                  </div>
                  <div>
                    <p className="text-center text-sm mb-2">Right</p>
                    <img src={faceCaptures.right} alt="Right view" className="w-full rounded-lg border-2 border-green-500" />
                  </div>
                </div>

                <div className="bg-green-900 bg-opacity-50 rounded-lg p-4 text-center">
                  <p className="text-green-300 font-bold">✓ All captures completed successfully!</p>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={retake3DCapture}
                    className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-3 rounded-lg"
                  >
                    Retake
                  </button>
                  <button
                    onClick={save3DFaceCapture}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-bold"
                  >
                    Save & Continue
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Selfie with ID Modal */}
      {showSelfieModal && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
          <div className="bg-purple-800 rounded-lg p-6 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Selfie with ID</h3>
              <button onClick={closeSelfieModal} className="text-purple-300 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
                <video
                  id="selfieVideo"
                  autoPlay
                  playsInline
                  muted
                  ref={(video) => {
                    if (video && selfieStream) {
                      video.srcObject = selfieStream;
                    }
                  }}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="bg-purple-700 rounded-lg p-4">
                <h4 className="font-bold mb-2">Instructions:</h4>
                <ul className="text-sm space-y-1">
                  <li>✓ Hold your ID card next to your face</li>
                  <li>✓ Make sure both your face and ID are clearly visible</li>
                  <li>✓ Click "Capture" when ready</li>
                </ul>
              </div>

              <button
                onClick={handleSelfieCaptureClick}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-bold"
              >
                📸 Capture Selfie
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddBankModal && <AddBankAccountModal />}
      {showCreateWalletModal && <CreateWalletModal />}
      {showTopUpCardModal && <TopUpCardModal />}
      {showCardHistoryModal && <CardHistoryModal />}
      
      {/* Send From Card Modal */}
      {showSendFromCardModal && selectedCardWallet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-purple-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Send from {selectedCardWallet.name}</h3>
              <button onClick={() => {
                setShowSendFromCardModal(false);
                setSelectedCardWallet(null);
                setWalletSendData({ crypto: 'BTC', recipientAddress: '', amount: '', note: '' });
              }} className="text-purple-300 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-purple-200 text-sm font-medium mb-2">
                  Select Cryptocurrency
                </label>
                <select
                  value={walletSendData.crypto}
                  onChange={(e) => setWalletSendData({...walletSendData, crypto: e.target.value})}
                  className="w-full px-4 py-2 bg-purple-700 border border-purple-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="BTC">Bitcoin (BTC)</option>
                  <option value="ETH">Ethereum (ETH)</option>
                  <option value="USDT">Tether (USDT)</option>
                  <option value="USDC">USD Coin (USDC)</option>
                </select>
              </div>

              <div>
                <label className="block text-purple-200 text-sm font-medium mb-2">
                  Recipient Address
                </label>
                <input
                  type="text"
                  value={walletSendData.recipientAddress}
                  onChange={(e) => setWalletSendData({...walletSendData, recipientAddress: e.target.value})}
                  placeholder="Enter recipient's wallet address"
                  className="w-full px-4 py-2 bg-purple-700 border border-purple-600 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-purple-200 text-sm font-medium mb-2">
                  Amount ({walletSendData.crypto})
                </label>
                <input
                  type="number"
                  step="0.00000001"
                  value={walletSendData.amount}
                  onChange={(e) => setWalletSendData({...walletSendData, amount: e.target.value})}
                  placeholder="0.00"
                  className="w-full px-4 py-2 bg-purple-700 border border-purple-600 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:border-blue-500"
                />
                <p className="text-sm text-purple-300 mt-1">
                  Available: {selectedCardWallet.balances?.[walletSendData.crypto] || '0.00000000'} {walletSendData.crypto}
                </p>
              </div>

              <div>
                <label className="block text-purple-200 text-sm font-medium mb-2">
                  Note (Optional)
                </label>
                <input
                  type="text"
                  value={walletSendData.note}
                  onChange={(e) => setWalletSendData({...walletSendData, note: e.target.value})}
                  placeholder="Payment for..."
                  className="w-full px-4 py-2 bg-purple-700 border border-purple-600 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div className="bg-purple-900 rounded-lg p-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-purple-300">Network Fee</span>
                  <span className="text-white">~0.0001 {walletSendData.crypto}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-purple-300">Total</span>
                  <span className="text-white font-bold">
                    {(parseFloat(walletSendData.amount) + 0.0001).toFixed(8)} {walletSendData.crypto}
                  </span>
                </div>
              </div>

              <button
                onClick={handleSendFromWallet}
                disabled={loading || !walletSendData.recipientAddress || !walletSendData.amount}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
              >
                <Send size={20} />
                <span>{loading ? 'Processing...' : `Send ${walletSendData.crypto}`}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Withdraw From Card Modal */}
      {showWithdrawFromCardModal && selectedCardWallet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-purple-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Withdraw from {selectedCardWallet.name}</h3>
              <button onClick={() => {
                setShowWithdrawFromCardModal(false);
                setSelectedCardWallet(null);
                setWalletWithdrawData({ crypto: 'BTC', amount: '', method: 'bank' });
              }} className="text-purple-300 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-purple-200 text-sm font-medium mb-2">
                  Select Cryptocurrency
                </label>
                <select
                  value={walletWithdrawData.crypto}
                  onChange={(e) => setWalletWithdrawData({...walletWithdrawData, crypto: e.target.value})}
                  className="w-full px-4 py-2 bg-purple-700 border border-purple-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="BTC">Bitcoin (BTC)</option>
                  <option value="ETH">Ethereum (ETH)</option>
                  <option value="USDT">Tether (USDT)</option>
                  <option value="USDC">USD Coin (USDC)</option>
                </select>
              </div>

              <div>
                <label className="block text-purple-200 text-sm font-medium mb-2">
                  Withdrawal Method
                </label>
                <div className="space-y-2">
                  <button 
                    onClick={() => setWalletWithdrawData({...walletWithdrawData, method: 'bank'})}
                    className={`w-full p-4 rounded-lg text-left transition-colors ${
                      walletWithdrawData.method === 'bank' 
                        ? 'bg-purple-600 border-2 border-blue-400' 
                        : 'bg-purple-700 hover:bg-purple-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Building size={24} className="text-blue-400" />
                        <div>
                          <p className="font-medium">Bank Account</p>
                          <p className="text-xs text-purple-300">1-3 business days</p>
                        </div>
                      </div>
                      <div className="text-sm text-green-400">Free</div>
                    </div>
                  </button>

                  <button 
                    onClick={() => setWalletWithdrawData({...walletWithdrawData, method: 'card'})}
                    className={`w-full p-4 rounded-lg text-left transition-colors ${
                      walletWithdrawData.method === 'card' 
                        ? 'bg-purple-600 border-2 border-yellow-400' 
                        : 'bg-purple-700 hover:bg-purple-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <CreditCard size={24} className="text-yellow-400" />
                        <div>
                          <p className="font-medium">Debit/Credit Card</p>
                          <p className="text-xs text-purple-300">Instant</p>
                        </div>
                      </div>
                      <div className="text-sm text-yellow-400">3% fee</div>
                    </div>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-purple-200 text-sm font-medium mb-2">
                  Amount ({walletWithdrawData.crypto})
                </label>
                <input
                  type="number"
                  step="0.00000001"
                  value={walletWithdrawData.amount}
                  onChange={(e) => setWalletWithdrawData({...walletWithdrawData, amount: e.target.value})}
                  placeholder="0.00"
                  className="w-full px-4 py-2 bg-purple-700 border border-purple-600 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:border-blue-500"
                />
                <p className="text-sm text-purple-300 mt-1">
                  Available: {selectedCardWallet.balances?.[walletWithdrawData.crypto] || '0.00000000'} {walletWithdrawData.crypto}
                </p>
              </div>

              <div>
                <label className="block text-purple-200 text-sm font-medium mb-2">
                  Estimated USD Amount
                </label>
                <input
                  type="text"
                  value={`$${((parseFloat(walletWithdrawData.amount) || 0) * (cryptoPrices[walletWithdrawData.crypto] || 0)).toFixed(2)}`}
                  readOnly
                  className="w-full px-4 py-2 bg-purple-900 border border-purple-600 rounded-lg text-white"
                />
              </div>

              <div className="bg-yellow-900 bg-opacity-50 border border-yellow-600 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="text-yellow-400 flex-shrink-0 mt-0.5" size={18} />
                  <p className="text-sm text-yellow-200">
                    Instant withdrawals to debit/credit cards incur a 3% processing fee. 
                    Bank transfers are free but take 1-3 business days.
                  </p>
                </div>
              </div>

              <button
                onClick={handleWithdrawFromWallet}
                disabled={loading || !walletWithdrawData.amount}
                className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
              >
                <ArrowDown size={20} />
                <span>{loading ? 'Processing...' : 'Continue Withdrawal'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
      
       <ActivateCardModal 
        showActivateCardModal={showActivateCardModal}
        setShowActivateCardModal={setShowActivateCardModal}
        activateData={activateData}
        setActivateData={setActivateData}
        onSuccess={async () => {
          await loadRealDataFromBackend();
          await loadWallets();
          await loadTransactions();
          if (!user) {
            setShowLandingPage(false);
          }
        }}
      />

        {/* Security Warning Modal */}
        {showSecurityWarning && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-gradient-to-br from-red-900 to-purple-900 rounded-lg p-8 max-w-2xl w-full">
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center space-x-3">
                  <AlertTriangle className="text-yellow-400" size={32} />
                  <h3 className="text-2xl font-bold text-white">⚠️ SECURITY WARNING</h3>
                </div>
                <button onClick={() => {setShowSecurityWarning(false); setSelectedWalletForKey(null);}} className="text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>
              <div className="bg-red-800 bg-opacity-50 rounded-lg p-6 mb-6 border-2 border-red-500">
                <h4 className="text-xl font-bold mb-4 text-white">🔐 CRITICAL: Private Key Security</h4>
                <ul className="space-y-3 text-white">
                  <li className="flex items-start space-x-2">
                    <span className="text-red-400 font-bold mt-1">•</span>
                    <span><strong>NEVER share your private key</strong> with anyone, including support staff</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-red-400 font-bold mt-1">•</span>
                    <span>Anyone with your private key has <strong>FULL CONTROL</strong> of your funds</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-red-400 font-bold mt-1">•</span>
                    <span>Store it securely offline (hardware wallet, paper backup)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-red-400 font-bold mt-1">•</span>
                    <span>Ensure no one is watching your screen or recording</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-red-400 font-bold mt-1">•</span>
                    <span><strong>Zanors will NEVER ask</strong> for your private key</span>
                  </li>
                </ul>
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={() => {setShowSecurityWarning(false); setSelectedWalletForKey(null);}}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={acknowledgeSecurityWarning}
                  className="flex-1 bg-red-600 hover:bg-red-700 px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                >
                  <Shield size={20} />
                  <span>I Understand, Show Keys</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Private Key Display Modal */}
        {showPrivateKeyModal && selectedWalletForKey && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-gradient-to-br from-purple-900 to-purple-800 rounded-lg p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-bold flex items-center space-x-2">
                  <Lock className="text-red-400" size={28} />
                  <span>Private Keys - {selectedWalletForKey.name}</span>
                </h3>
                <button onClick={() => {setShowPrivateKeyModal(false); setSelectedWalletForKey(null);}} className="text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>
              <div className="space-y-4">
                {['BTC', 'ETH', 'XRP'].map((crypto) => (
                  <div key={crypto} className="bg-purple-700 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-lg font-bold">{crypto} Private Key</h4>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => toggleKeyVisibility(`${selectedWalletForKey.id}-${crypto}`)}
                          className="bg-purple-600 hover:bg-purple-500 px-3 py-2 rounded-lg flex items-center space-x-1 transition-colors"
                        >
                          {visibleKeys[`${selectedWalletForKey.id}-${crypto}`] ? <EyeOff size={18} /> : <Eye size={18} />}
                          <span>{visibleKeys[`${selectedWalletForKey.id}-${crypto}`] ? 'Hide' : 'Show'}</span>
                        </button>
                        {revealedKeys[`${selectedWalletForKey.id}-${crypto}`] && (
                          <button
                            onClick={() => copyToClipboard(revealedKeys[`${selectedWalletForKey.id}-${crypto}`])}
                            className="bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded-lg flex items-center space-x-1 transition-colors"
                          >
                            {copied === revealedKeys[`${selectedWalletForKey.id}-${crypto}`] ? <Check size={18} /> : <Copy size={18} />}
                            <span>Copy</span>
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="bg-purple-900 rounded-lg p-4 font-mono text-sm break-all">
                      {!revealedKeys[`${selectedWalletForKey.id}-${crypto}`] ? (
                        <button
                          onClick={() => revealPrivateKey(selectedWalletForKey.id, crypto)}
                          disabled={loadingPrivateKey}
                          className="w-full bg-red-600 hover:bg-red-700 py-2 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {loadingPrivateKey ? 'Loading...' : `Click to Reveal ${crypto} Private Key`}
                        </button>
                      ) : visibleKeys[`${selectedWalletForKey.id}-${crypto}`] ? (
                        <span className="text-green-400">{revealedKeys[`${selectedWalletForKey.id}-${crypto}`]}</span>
                      ) : (
                        <span className="text-gray-500">{'•'.repeat(64)}</span>
                      )}
                    </div>
                    <p className="text-xs text-red-400 mt-2 flex items-center space-x-1">
                      <AlertTriangle size={14} />
                      <span>Never share this key. Anyone with it can access your funds.</span>
                    </p>
                  </div>
                ))}
              </div>
              <button
                onClick={() => {setShowPrivateKeyModal(false); setSelectedWalletForKey(null);}}
                className="w-full mt-6 bg-purple-600 hover:bg-purple-700 py-3 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
    </div>
  );
};
function App() {
  // Get user state for pages that need authentication
  const [user, setUser] = useState(null);

  // Listen for user auth changes
  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
      }
    };
    
    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <Router>
      <Routes>
        <Route path="/" element={<ZANORSApp />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/company" element={<CompanyPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/send-money" element={<SendMoneyPage user={user} />} />
        <Route path="/crypto-cards" element={<CryptoCardsPage />} />
        <Route path="/digital-wallets" element={<DigitalWalletsPage />} />
        <Route path="/business-solutions" element={<BusinessSolutionsPage />} />
        <Route path="/api-documentation" element={<APIDocumentationPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/about-us" element={<AboutUsPage />} />
        <Route path="/careers" element={<CareersPage />} />
        <Route path="/compliance" element={<CompliancePage />} />
        <Route path="/licenses" element={<LicensePage />} />
        <Route path="/press" element={<PressPage />} />
        <Route path="/investors" element={<InvestorsPage />} />
        <Route path="/help-center" element={<HelpCenterPage />} />
        <Route path="/contact-us" element={<ContactUsPage />} />
        <Route path="/shop" element={<CryptoCardShop />} />
        <Route path="/batch-payments" element={<BatchPaymentsPage user={user} />} /> {/* ✅ Batch Payments standalone route */}
        <Route path="/gp-secret-admin-2025" element={<AdminPanel />} />
      </Routes>
    </Router>
  );
}

export default App;